/**
 * question-runtime.js
 * 题目交互运行时（前端注入到 iframe srcdoc）
 * 职责：交互引擎 + 提交收集 + 回显恢复 + 语音讲解
 * 设计：按 [data-question-id] 容器隔离，document 级事件委托路由
 * answer 分隔符与后端 extract_correct_answer_from_html / 批改函数对齐：
 *   drag/match/sort/memory 用 , ；fill 用 |
 */
// 注入前清场：移除 AI 写的 script/on*/javascript:（前端字符串处理，非注入到 iframe 的代码）
export function stripAllScripts(html) {
  if (!html) return '';
  // lecture 讲解页保留 AI 写的 JS（动画演示等），不 strip
  if (/data-question-type=["']?lecture["']?/i.test(html)) return html;
  html = html.replace(/<script[\s\S]*?<\/script>/gi, '');
  html = html.replace(/\son\w+\s*=\s*"[^"]*"/gi, '');
  html = html.replace(/\son\w+\s*=\s*'[^']*'/gi, '');
  html = html.replace(/\son\w+\s*=\s*[^\s>]+/gi, '');
  html = html.replace(/(href|src)\s*=\s*["']javascript:[^"']*["']/gi, '$1="#"');
  return html;
}

// KaTeX 公式注入（CSS + JS + auto-render + mhchem），本地化资源 /static/formula/
// KaTeX: 渲染 HTML 中的 $...$ 和 $$...$$ 公式（含 mhchem 化学式）
// 公式输入已统一为"普通输入框 + ✍手写公式"（MathLive/math-field 已整体退役移除，存量题目为测试数据不保留兼容）
export const katexInject = [
  '<link rel="stylesheet" href="/static/formula/katex/katex.min.css">',
  '<script src="/static/formula/katex/katex.min.js"><\/script>',
  '<script src="/static/formula/katex/contrib/mhchem.min.js"><\/script>',
  '<script src="/static/formula/katex/contrib/auto-render.min.js"><\/script>',
  '<script>',
  // 用户在题目内交互时通知宿主页（学习成果面板据此自动收缩）；每次指针按下都广播，宿主幂等处理
  'document.addEventListener("pointerdown", function() { try { parent.postMessage({ type: "question-interact" }, parent.location.origin); } catch (e) {} }, true);',
  'document.addEventListener("DOMContentLoaded", function() {',
  '  if (typeof renderMathInElement === "function") {',
  '    renderMathInElement(document.body, {',
  '      delimiters: [',
  '        {left: "$$", right: "$$", display: true},',
  '        {left: "$", right: "$", display: false}',
  '      ],',
  '      throwOnError: false',
  '    });',
  '  }',
  '});',
  '<\/script>'
].join('\n');

// MathLive 已整体退役（公式输入=普通输入框+手写，公式展示=KaTeX）。
// 备课平台 ChatPanel 按历史约定单独 import 本导出，保留空串避免拼入 "undefined" 字面量。
export const mathliveInject = '';

// 公式→口语转换（TTS 不支持公式朗读）：单一事实源在 speech.js，运行时字符串内嵌其源码
import { toSpeakableText } from './speech.js';

export default `
(function() {
  // ====== 工具函数 ======
  // 公式→口语(TTS不支持公式朗读)：由 speech.js 的 toSpeakableText.toString() 内嵌，修改请去 speech.js
  var toSpeakableText = ${toSpeakableText.toString()};
  function getQuestion(el) { return el && el.closest ? el.closest('[data-question-id]') : null; }
  // 交互引擎归属容器：复合题（multi）的空容器带 data-sub-id 无 data-question-id，
  // 拖拽/排序/分类引擎按此查找最近的承载容器（题容器或空容器）
  function getScope(el) { return el && el.closest ? el.closest('[data-question-id], [data-sub-id]') : null; }
  function getQType(q) { return q ? (q.getAttribute('data-question-type') || '') : ''; }
  // LaTeX 答案归一化（用于填空题公式比对）
  function normalizeLatexAnswer(s) {
    if (!s) return '';
    s = String(s).trim();
    s = s.replace(/\\s+/g, '');           // 去所有空白
    s = s.replace(/\\\\cdot/g, '*');       // \\cdot → *
    s = s.replace(/\\\\times/g, '*');      // times → *
    s = s.replace(/\\^\\{(\\w)\\}/g, '^$1'); // x^{2} → x^2（单字符去花括号）
    s = s.replace(/_\\{(\\w)\\}/g, '_$1');  // x_{1} → x_1
    s = s.replace(/\\\\left/g, '').replace(/\\\\right/g, ''); // left right 去掉
    return s.toLowerCase();
  }
  function getCorrectAttr(q) { return q ? (q.getAttribute('data-correct-answer') || '') : ''; }
  function getCorrectArr(q) { var v = getCorrectAttr(q); return v ? v.split(',') : []; }
  function findSubmitBtn(q, qtype) { return q.querySelector('[data-submit="' + qtype + '"]') || q.querySelector('[data-submit]'); }

  // 反馈区兜底门控：AI生成的题目HTML偶发把参考答案/反馈区默认可见（未作答就剧透）。
  // 运行时统一隐藏，提交后显示；回显已提交状态时经 restore→submit click 走到同一显示逻辑。
  function feedbackPanels(q) { return q.querySelectorAll('[class*="feedback"], [data-feedback]'); }
  function hideFeedback(q) {
    // 仅交互题（有提交按钮）隐藏；讲解类/无提交按钮的页面不干预
    var qtype = getQType(q);
    if (!qtype || !findSubmitBtn(q, qtype)) return;
    feedbackPanels(q).forEach(function(el) { el.setAttribute('data-feedback-hidden', '1'); el.style.display = 'none'; });
  }
  function showFeedback(q) {
    feedbackPanels(q).forEach(function(el) {
      if (el.getAttribute('data-feedback-hidden') === '1') { el.style.display = ''; el.removeAttribute('data-feedback-hidden'); }
    });
  }

  // 拖拽时临时重置祖先的 transform/perspective/filter，避免破坏 position:fixed 定位
  function resetFixedAncestors(el) {
    var affected = [];
    var p = el.parentNode;
    while (p && p !== document.body) {
      var cs = window.getComputedStyle(p);
      if (cs.transform !== 'none' || cs.perspective !== 'none' || (cs.filter && cs.filter !== 'none')) {
        affected.push({ node: p, t: p.style.transform, pe: p.style.perspective, f: p.style.filter });
        p.style.transform = 'none'; p.style.perspective = 'none'; p.style.filter = 'none';
      }
      p = p.parentNode;
    }
    return affected;
  }
  function restoreFixedAncestors(affected) {
    affected.forEach(function(a) { a.node.style.transform = a.t; a.node.style.perspective = a.pe; a.node.style.filter = a.f; });
  }

  // ====== 1. 拖拽引擎（drag）======
  function initDrag(q) { q._dragState = { dragEl: null, origParent: null, origNext: null, offsetX: 0, offsetY: 0, affected: [] }; }
  function dragStart(e) {
    var el = e.target.closest('[data-drag-id]'); if (!el) return;
    var q = getScope(el); if (!q || !q._dragState) return;
    e.preventDefault();
    var st = q._dragState;
    st.dragEl = el; st.origParent = el.parentNode; st.origNext = el.nextSibling;
    var rect = el.getBoundingClientRect();
    var pt = e.touches ? e.touches[0] : e;
    st.offsetX = pt.clientX - rect.left; st.offsetY = pt.clientY - rect.top;
    st.affected = resetFixedAncestors(el);
    el.style.position = 'fixed'; el.style.zIndex = '9999';
    el.style.left = (pt.clientX - st.offsetX) + 'px'; el.style.top = (pt.clientY - st.offsetY) + 'px';
    el.style.margin = '0'; el.style.pointerEvents = 'none';
    // 题目CSS常带 transition:all 和 :hover transform，会让拖拽元素滞后/偏移鼠标，拖拽期间禁用
    el.style.transition = 'none'; el.style.transform = 'none';
    window.__activeDragQ = q;
  }
  function dragMove(e) {
    var el = e.target.closest('[data-drag-id]');
    var q = el ? getScope(el) : (window.__activeDragQ || null);
    if (!q || !q._dragState || !q._dragState.dragEl) return;
    e.preventDefault();
    var st = q._dragState; var pt = e.touches ? e.touches[0] : e;
    st.dragEl.style.left = (pt.clientX - st.offsetX) + 'px'; st.dragEl.style.top = (pt.clientY - st.offsetY) + 'px';
    var cy = pt.clientY;
    if (cy < 80) window.scrollBy(0, -15 * (1 - cy / 80));
    else if (cy > window.innerHeight - 80) window.scrollBy(0, 15 * (1 - (window.innerHeight - cy) / 80));
  }
  function dragEnd(e) {
    var st = null, q = null;
    document.querySelectorAll('[data-question-id], [data-sub-id]').forEach(function(eq) {
      if (eq._dragState && eq._dragState.dragEl) { q = eq; st = eq._dragState; }
    });
    if (!st || !st.dragEl) return;
    var pt = e.changedTouches ? e.changedTouches[0] : e;
    st.dragEl.style.display = 'none';
    var below = document.elementFromPoint(pt.clientX, pt.clientY);
    st.dragEl.style.display = '';
    st.dragEl.style.position = ''; st.dragEl.style.zIndex = '';
    st.dragEl.style.left = ''; st.dragEl.style.top = ''; st.dragEl.style.margin = ''; st.dragEl.style.pointerEvents = '';
    st.dragEl.style.transition = ''; st.dragEl.style.transform = '';
    var target = below ? below.closest('[data-target-id]') : null;
    if (target) {
      var oldItem = target.querySelector('[data-drag-id]');
      if (oldItem && oldItem !== st.dragEl) { if (st.origNext) st.origParent.insertBefore(oldItem, st.origNext); else st.origParent.appendChild(oldItem); }
      target.appendChild(st.dragEl);
    } else { if (st.origNext) st.origParent.insertBefore(st.dragEl, st.origNext); else st.origParent.appendChild(st.dragEl); }
    restoreFixedAncestors(st.affected);
    st.dragEl = null; st.origParent = null; st.origNext = null; st.affected = [];
    window.__activeDragQ = null;
  }

  // ====== 2. 排序引擎（sort）======
  // 排序容器定位（2026-10-28 根因修复）：优先规范类名 .sort-container；生成侧 LLM 偶发自创
  // 容器类名（如 .sort-area）或直接散放卡片——回落取首个 .sort-item 的父节点，保证
  // placeholder 安放与落位插入不因容器类名漂移而失效（原三处硬依赖致"能拖起但排不了"）
  function sortContainer(q) {
    var c = q.querySelector('.sort-container');
    if (c) return c;
    var item = q.querySelector('.sort-item');
    return item ? item.parentNode : null;
  }
  function initSort(q) {
    q._sortState = { dragEl: null, placeholder: null, offsetY: 0, affected: [] };
    q._isSlotMode = !!q.querySelector('[data-slot]');
    var container = sortContainer(q);
    if (container) {
      container.addEventListener('click', function(e) {
        var btn = e.target.closest('.sort-up,.sort-down'); if (!btn || q._sortState.dragEl) return;
        var item = btn.closest('.sort-item');
        if (btn.classList.contains('sort-up') && item.previousElementSibling) container.insertBefore(item, item.previousElementSibling);
        else if (btn.classList.contains('sort-down') && item.nextElementSibling) container.insertBefore(item.nextElementSibling, item);
        updateSortNums(q);
      });
    }
  }
  function updateSortNums(q) {
    q.querySelectorAll('.sort-item').forEach(function(el, i) { var num = el.querySelector('.sort-num'); if (num) num.textContent = i + 1; });
  }
  function sortStart(e) {
    var el = e.target.closest('.sort-item'); if (!el) return;
    var q = getScope(el); if (!q || !q._sortState || q._isSlotMode) return;
    e.preventDefault();
    var st = q._sortState; st.dragEl = el;
    var pt = e.touches ? e.touches[0] : e;
    st.offsetY = pt.clientY - el.getBoundingClientRect().top;
    st.affected = resetFixedAncestors(el);
    el.style.position = 'fixed'; el.style.zIndex = '9999';
    el.style.left = el.getBoundingClientRect().left + 'px'; el.style.top = (pt.clientY - st.offsetY) + 'px';
    el.style.width = el.getBoundingClientRect().width + 'px'; el.style.opacity = '0.85';
    el.style.pointerEvents = 'none'; el.style.margin = '0';
    // 拖拽期间禁用题目CSS的 transition/transform，保证跟随鼠标
    el.style.transition = 'none'; el.style.transform = 'none';
    st.placeholder = document.createElement('div');
    st.placeholder.className = 'sort-placeholder';
    st.placeholder.style.cssText = 'height:' + el.getBoundingClientRect().height + 'px;background:rgba(0,0,0,0.1);border:2px dashed #999;border-radius:6px;margin:4px 0;';
    var container = sortContainer(q);
    if (container) container.insertBefore(st.placeholder, el.nextSibling);
  }
  function sortMove(e) {
    var q = null;
    document.querySelectorAll('[data-question-id], [data-sub-id]').forEach(function(eq) { if (eq._sortState && eq._sortState.dragEl) q = eq; });
    if (!q || !q._sortState.dragEl) return;
    e.preventDefault();
    var st = q._sortState; var pt = e.touches ? e.touches[0] : e;
    st.dragEl.style.top = (pt.clientY - st.offsetY) + 'px';
    var cy = pt.clientY;
    if (cy < 80) window.scrollBy(0, -15 * (1 - cy / 80));
    else if (cy > window.innerHeight - 80) window.scrollBy(0, 15 * (1 - (window.innerHeight - cy) / 80));
    var below = document.elementFromPoint(pt.clientX, pt.clientY);
    var item = below ? below.closest('.sort-item') : null;
    var container = sortContainer(q);
    if (item && item !== st.dragEl && container) container.insertBefore(st.placeholder, item);
  }
  function sortEnd(e) {
    var q = null;
    document.querySelectorAll('[data-question-id], [data-sub-id]').forEach(function(eq) { if (eq._sortState && eq._sortState.dragEl) q = eq; });
    if (!q || !q._sortState.dragEl) return;
    var st = q._sortState;
    st.dragEl.style.position = ''; st.dragEl.style.zIndex = ''; st.dragEl.style.left = ''; st.dragEl.style.top = '';
    st.dragEl.style.width = ''; st.dragEl.style.opacity = ''; st.dragEl.style.pointerEvents = ''; st.dragEl.style.margin = '';
    st.dragEl.style.transition = ''; st.dragEl.style.transform = '';
    if (st.placeholder && st.placeholder.parentNode) { st.placeholder.parentNode.insertBefore(st.dragEl, st.placeholder); st.placeholder.parentNode.removeChild(st.placeholder); }
    restoreFixedAncestors(st.affected);
    st.dragEl = null; st.placeholder = null; st.affected = [];
    updateSortNums(q);
  }

  // ====== 3. 分类引擎（classify）——2026-10-28 退役删除：与拖拽匹配(drag)功能重叠，用户拍板
  // 题型下线（字典 question_type_html.classify 已 status=0，存量不留兼容） ======

  // ====== 4. 连线引擎（match）—— 新写，点击选中+SVG画线 ======
  function initMatch(q) {
    q._matchState = { selectedSource: null };
    q.addEventListener('click', function(e) {
      var st = q._matchState;
      var src = e.target.closest('[data-match-source]');
      var tgt = e.target.closest('[data-match-target]');
      if (src) {
        if (src.getAttribute('data-connected-target')) { src.removeAttribute('data-connected-target'); src.classList.remove('match-selected'); drawMatchLines(q); if (st.selectedSource) { st.selectedSource.classList.remove('match-selected'); st.selectedSource = null; } return; }
        if (st.selectedSource) st.selectedSource.classList.remove('match-selected');
        st.selectedSource = src; src.classList.add('match-selected'); return;
      }
      if (tgt && st.selectedSource) {
        st.selectedSource.setAttribute('data-connected-target', tgt.getAttribute('data-match-target'));
        st.selectedSource.classList.remove('match-selected');
        st.selectedSource = null; drawMatchLines(q); return;
      }
      if (st.selectedSource) { st.selectedSource.classList.remove('match-selected'); st.selectedSource = null; }
    });
  }
  function drawMatchLines(q) {
    var svg = q.querySelector('svg.match-lines'); if (!svg) return;
    // 剥 viewBox(2026-11-03 连线乱根因修复)：LLM 生成的 match 题面常自带 <svg class="match-lines"
    // viewBox="0 0 80 300" preserveAspectRatio="none"> 骨架(原配被剥掉的自带JS用)，本函数按视口
    // 像素坐标直写 line 的 x/y——viewBox 会把用户坐标系拉伸映射到 SVG 实际尺寸(如 80→764 横向
    // 9.55 倍)，坐标被变换后线端点全数飘出可视区(实测学生端观潮连线匹配回显四线全乱)。幂等：重复
    // 画线/resize 重画/订正回显多次执行无副作用；viewBox 移除后 preserveAspectRatio 失效不用动
    if (svg.getAttribute('viewBox')) svg.removeAttribute('viewBox');
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    var svgRect = svg.getBoundingClientRect();
    q.querySelectorAll('[data-match-source]').forEach(function(src) {
      var tid = src.getAttribute('data-connected-target'); if (!tid) return;
      var tgt = q.querySelector('[data-match-target="' + tid + '"]'); if (!tgt) return;
      var sR = src.getBoundingClientRect(); var tR = tgt.getBoundingClientRect();
      // 端点取两卡相对面(2026-11-03 连线穿卡修复)：source/target 列位左右两种题面都有(词语左
      // source/解释右 target 与 词语左 target/解释右 source)，原实现写死"source右缘→target左缘"
      // (假设 source 恒在左列)——source 在右列时线从画面右缘横贯左列左缘，压过两列所有卡片。
      // 按两卡中心点定左右，线落在两卡相邻面之间
      var sOnLeft = (sR.left + sR.width / 2) <= (tR.left + tR.width / 2);
      var x1 = sOnLeft ? (sR.left - svgRect.left + sR.width) : (sR.left - svgRect.left);
      var y1 = sR.top - svgRect.top + sR.height / 2;
      var x2 = sOnLeft ? (tR.left - svgRect.left) : (tR.left - svgRect.left + tR.width);
      var y2 = tR.top - svgRect.top + tR.height / 2;
      var line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', x1); line.setAttribute('y1', y1); line.setAttribute('x2', x2); line.setAttribute('y2', y2);
      line.setAttribute('stroke', '#4CAF50'); line.setAttribute('stroke-width', '2');
      svg.appendChild(line);
    });
  }

  // ====== 5. 翻牌引擎（memory）—— 点击翻开+配对 ======
  // 配对真源=data-correct-answer「卡片ID-卡片ID」配对串（2026-11-02 契约收口）：翻开第二张时查配对表
  // 判是否同一对，不再依赖 LLM 写的 data-card-group（set 2364368931247185 两版产物实测：组号分别为
  // 8卡8组/配对序号，均与答案串无关——组号驱动下要么永远翻不对、要么与批改侧对不上）。
  // 对内无向（1-5 ≡ 5-1，规范键小ID在前），与批改侧 _undirected_pair 同口径；配上的两张卡写
  // data-matched-pair=规范键，供 collectMemory 收集与回显恢复
  function memoryPairKey(a, b) { a = String(a).trim(); b = String(b).trim(); return a < b ? a + '-' + b : b + '-' + a; }
  function memoryPairSet(q) {
    var set = {};
    getCorrectArr(q).forEach(function(p) {
      var parts = p.split('-');
      if (parts.length === 2 && parts[0].trim() && parts[1].trim()) set[memoryPairKey(parts[0], parts[1])] = true;
    });
    return set;
  }
  function initMemory(q) {
    q._memoryState = { flipped: [], busy: false };
    q.addEventListener('click', function(e) {
      var card = e.target.closest('[data-card-id]'); if (!card) return;
      var st = q._memoryState;
      if (st.busy || card.classList.contains('matched') || card.classList.contains('flipped')) return;
      card.classList.add('flipped'); st.flipped.push(card);
      if (st.flipped.length === 2) {
        st.busy = true;
        var a = st.flipped[0], b = st.flipped[1];
        var key = memoryPairKey(a.getAttribute('data-card-id'), b.getAttribute('data-card-id'));
        if (memoryPairSet(q)[key]) {
          a.classList.add('matched'); b.classList.add('matched');
          a.setAttribute('data-matched-pair', key); b.setAttribute('data-matched-pair', key);
          st.flipped = []; st.busy = false;
        } else {
          setTimeout(function() { a.classList.remove('flipped'); b.classList.remove('flipped'); st.flipped = []; st.busy = false; }, 800);
        }
      }
    });
  }

  // ====== 行为埋点：pause_count / revision_count ======
  function initBehaviorTracking(q) {
    q._behavior = { lastInteraction: Date.now(), pauseCount: 0, revisionCount: 0 };
    q.addEventListener('click', function() {
      var now = Date.now(); var st = q._behavior;
      if (now - st.lastInteraction > 3000) st.pauseCount++;
      st.lastInteraction = now;
    });
    q.addEventListener('input', function() {
      var st = q._behavior; st.revisionCount++;
      st.lastInteraction = Date.now();
    });
  }
  function getBehaviorData(q) {
    var st = q._behavior || {};
    return { pause_count: st.pauseCount || 0, revision_count: st.revisionCount || 0 };
  }

  // ====== 6. 选择/判断（点击高亮）======
  function initChoice(q) {
    var subGroups = q.querySelectorAll('[data-sub-id]');
    q.querySelectorAll('[data-option]').forEach(function(opt) {
      opt.addEventListener('click', function() {
        // 提交/回显后锁定（data-locked，见 6b 节）：已判分的选择不可再改
        if (opt.closest && opt.closest('[data-locked]')) return;
        // 一题多空（data-sub-id 分组）：互斥范围限定在本子题组内；否则整题互斥
        var scope = (subGroups.length && opt.closest && opt.closest('[data-sub-id]')) || q;
        scope.querySelectorAll('[data-option]').forEach(function(o) { o.classList.remove('selected'); o.setAttribute('data-selected', 'false'); });
        opt.classList.add('selected');
        opt.setAttribute('data-selected', 'true');
      });
    });
  }

  // ====== 6b. 选择题结果固定：提交/回显后锁定选择并对选中项差异化着色（对绿/错红） ======
  // collector-correct/wrong 与 answer-collector 的样式类同名同视觉；此处再注入一份，
  // 兜底无 collector 的场景（如教师端预览）。双份同规则幂等无冲突。
  // CSP阶段二: 优先可构造样式表(CSSOM落地, 无unsafe-inline的严格CSP下不被拦), 老浏览器退化<style>元素
  function ensureChoiceMarkStyles() {
    if (window.__choiceMarkStyles) return;
    window.__choiceMarkStyles = 1;
    var css = '.collector-correct { outline: 3px solid #10b981 !important; background: #d1fae5 !important; } .collector-wrong { outline: 3px solid #ef4444 !important; background: #fee2e2 !important; } .anno-hint { color: #94a3b8; }';
    if (typeof CSSStyleSheet !== 'undefined' && document.adoptedStyleSheets) {
      var sheet = new CSSStyleSheet();
      sheet.replace(css).catch(function () {});
      document.adoptedStyleSheets = Array.prototype.slice.call(document.adoptedStyleSheets).concat([sheet]);
    } else {
      var style = document.createElement('style');
      style.textContent = css;
      document.head.appendChild(style);
    }
  }
  // 给 scope 内当前选中项（学生所选）加对/错着色；未作答不着色；isCorrect 缺失（老数据）只锁定不着色
  function markChoiceResult(scope, isCorrect) {
    ensureChoiceMarkStyles();
    if (isCorrect === undefined || isCorrect === null) return;
    var sel = scope.querySelector('[data-option].selected, .judge-btn.selected');
    if (sel) { sel.classList.remove('collector-correct', 'collector-wrong'); sel.classList.add(isCorrect ? 'collector-correct' : 'collector-wrong'); }
  }
  // 锁定 scope 内选择：data-locked 挡 initChoice 切换；原生 radio/checkbox 一并禁用防浏览器原生切换
  function lockChoiceScope(scope) {
    scope.setAttribute('data-locked', '1');
    scope.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(function(el) { el.disabled = true; });
  }
  // 订正重试：解锁全部并清着色（与 answer-collector 的 resetForRetry 对齐）
  function unlockAllChoiceScopes() {
    document.querySelectorAll('[data-locked]').forEach(function(el) {
      el.removeAttribute('data-locked');
      el.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(function(i) { i.disabled = false; });
    });
    document.querySelectorAll('.collector-correct, .collector-wrong').forEach(function(el) { el.classList.remove('collector-correct', 'collector-wrong'); });
  }
  // 对 multi 题内全部 choice/judge 空：按提交数据 subAnswers[sid].isCorrect 着色并锁定（提交与回显共用）
  function markMultiChoiceSubs(q, subAnswers) {
    var sa = subAnswers || {};
    q.querySelectorAll('[data-sub-id]').forEach(function(sub) {
      var sid = sub.getAttribute('data-sub-id');
      var st = sub.getAttribute('data-sub-type') || '';
      if (st !== 'choice' && st !== 'judge') return;
      // 未作答空（无选中项）只锁定不着色
      if (sub.querySelector('.selected')) markChoiceResult(sub, sa[sid] && sa[sid].isCorrect);
      lockChoiceScope(sub);
    });
  }

  // ===== 手写公式输入：笔迹采集/预览/回显 =====
  // 注：不用 MathLive 公式编辑器——其虚拟键盘在 iframe 里依赖顶层窗口桥接（iframe 内禁止本地 container），
  // 学生端题目在 srcdoc iframe 渲染、顶层未加载 MathLive，键盘打不开。手写笔迹随答案提交，
  // 批改侧按后端统一 dots 格式（handwrite_dots）绘制识别，回显用 strokes 重绘。
  // 笔迹数据结构 { w, h, strokes:[[[x,y],...],...], image:'data:image/png;base64,...' }
  function drawFormulaStrokes(canvas, hw, cssW, cssH) {
    var dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(hw.w * dpr); canvas.height = Math.round(hw.h * dpr);
    canvas.style.width = cssW + 'px'; canvas.style.height = cssH + 'px';
    var ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, hw.w, hw.h);
    ctx.strokeStyle = '#1f2d3d'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    hw.strokes.forEach(function(stroke, si) {
      // 教师演示笔迹分色(2026-10-29): hw.colors 平行数组携带每笔颜色(老师红/学生黑),
      // 学生数据无 colors 数组——逐笔回落默认黑, 学生端渲染零变化
      ctx.strokeStyle = (hw.colors && hw.colors[si]) || '#1f2d3d';
      ctx.beginPath();
      stroke.forEach(function(p, i) { if (i === 0) ctx.moveTo(p[0], p[1]); else ctx.lineTo(p[0], p[1]); });
      ctx.stroke();
    });
  }
  // 预览锚点唯一 id：生成的填空 input 常无 id（仅 data-fill-id），无 id 时补一个，否则预览静默不渲染
  var __fhwUid = 0;
  function ensureFormulaHwId(el) {
    if (!el.id) el.id = '__fhw_' + (++__fhwUid);
    return el.id;
  }
  function renderFormulaHwPreview(el) {
    var hw = el.__formulaHandwriting;
    if (!hw) return;
    ensureFormulaHwId(el);
    var old = document.querySelector('.formula-hw-preview[data-for="' + el.id + '"]');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    var wrap = document.createElement('div');
    wrap.className = 'formula-hw-preview';
    wrap.setAttribute('data-for', el.id);
    wrap.title = '手写输入（点击可修改）';
    wrap.style.cssText = 'display:inline-block;vertical-align:middle;margin-left:6px;margin-top:4px;background:#fff;border:1px solid #c0c4cc;border-radius:6px;padding:2px;cursor:pointer;';
    var cvs = document.createElement('canvas');
    var dispH = 56;
    var dispW = Math.max(40, Math.min(Math.round(hw.w * dispH / hw.h), 220));
    drawFormulaStrokes(cvs, hw, dispW, dispH);
    wrap.appendChild(cvs);
    // 与「✍ 手写输入」按钮同行为：readOnly 在手写模式下是键盘互斥锁（作答插入/回显均置true），
    // 不能作为禁开面板信号，否则缩略图在回显态永远点不开（教师端查看笔迹即断）
    wrap.addEventListener('click', function() { openFormulaPopup(el); });
    var next = el.nextElementSibling;
    while (next && next.classList && (next.classList.contains('formula-insert-btn') || (next.classList.contains('formula-hw-preview')))) next = next.nextElementSibling;
    if (next && next.parentNode) el.parentNode.insertBefore(wrap, next); else el.parentNode.appendChild(wrap);
  }
  // 手写公式面板按钮统一样式：与页面“上一步/下一步”导航同款（藏青实底白字胶囊）
  var FH_BTN_CSS = 'box-sizing:border-box;height:32px;min-width:88px;padding:0 14px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #17376f;border-radius:999px;background:#17376f;color:#fff;font-family:inherit;font-size:13px;font-weight:600;line-height:1;text-align:center;cursor:pointer;box-shadow:0 3px 12px rgba(11,57,116,0.24);';
  // 大幅手写板：前景书写区尽量大、后景半透明灰、点灰色区域退出
  // 智能笔书写模式(2026-10-23): 顶层组件订阅学生手动绑定的笔MAC, WebSocket stroke 按笔转发进iframe,
  // 这里收点阵纸坐标笔迹→动态包围盒归一化实时预览→「✓插入」时等比缩放到HW逻辑空间与触屏笔迹同构,
  // 此后提交导出handwrite_dots/上传OSS/批改识别/回显全走触屏既有链路(question-runtime外零改动)。
  // 模块级状态: 弹窗重复打开时旧overlay被直接移除DOM(不走closePopup), 笔模式与监听必须在此层清理
  var __fhwPenActive = false;
  var __fhwPenListener = null;
  function __detachPenListener() {
    if (__fhwPenListener) {
      window.removeEventListener('message', __fhwPenListener);
      __fhwPenListener = null;
    }
  }
  function __stopPenChannel() {
    if (!__fhwPenActive) return;
    __fhwPenActive = false;
    window.parent.postMessage({ type: 'pen_write_stop' }, window.parent.location.origin);
  }
  // 智能笔笔迹联合包围盒等比缩放+居中到W×H逻辑空间(预览动态归一化与插入终态共用同一转换)
  function normalizePenItems(penItems, W, H) {
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    penItems.forEach(function(it) {
      it.pts.forEach(function(p) {
        if (p[0] < minX) minX = p[0];
        if (p[0] > maxX) maxX = p[0];
        if (p[1] < minY) minY = p[1];
        if (p[1] > maxY) maxY = p[1];
      });
    });
    var bw = maxX - minX, bh = maxY - minY;
    if (!isFinite(bw) || !isFinite(bh) || bw <= 0 || bh <= 0) return [];
    var scale = Math.min(W / bw, H / bh);
    var offX = (W - bw * scale) / 2 - minX * scale;
    var offY = (H - bh * scale) / 2 - minY * scale;
    return penItems.map(function(it) {
      return it.pts.map(function(p) {
        return [Math.round(p[0] * scale + offX), Math.round(p[1] * scale + offY)];
      });
    });
  }
  // 笔迹列表items: {src:'touch'|'penbase'|'pen', pts}。单一来源约束: 触屏与智笔数据不允许混在一起,
  // 切换输入方式即清掉另一来源(板上恒为同源); touch/penbase=HW逻辑空间原样(penbase=历史智笔笔迹基座),
  // pen=本会话智笔新迹(点阵纸坐标)——与基座坐标系完全不同, 续写绝不联合归一(会缩放改写历史内容):
  // 基座恒定, 新迹按自身包围盒等比缩放追加到基座右侧剩余区(不足转下方新行, 板满退化联合归一保可见)
  function layoutPenSessionStrokes(baseStrokes, sessionItems, W, H) {
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    sessionItems.forEach(function(it) {
      it.pts.forEach(function(p) {
        if (p[0] < minX) minX = p[0];
        if (p[0] > maxX) maxX = p[0];
        if (p[1] < minY) minY = p[1];
        if (p[1] > maxY) maxY = p[1];
      });
    });
    var sw = maxX - minX, sh = maxY - minY;
    if (!isFinite(sw) || !isFinite(sh)) return [];
    var GAP = 24;
    var sx0 = 0, sy0 = 0, sx1 = W, sy1 = H, centered = true;
    if (baseStrokes.length) {
      centered = false;
      var bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
      baseStrokes.forEach(function(pts) {
        pts.forEach(function(p) {
          if (p[0] < bx0) bx0 = p[0];
          if (p[0] > bx1) bx1 = p[0];
          if (p[1] < by0) by0 = p[1];
          if (p[1] > by1) by1 = p[1];
        });
      });
      if (W - bx1 - GAP >= 60) {
        // 同行右侧续写: 顶与基座对齐
        sx0 = bx1 + GAP; sy0 = by0; sx1 = W; sy1 = H;
      } else if (H - by1 - GAP >= 40) {
        // 右侧放不下转下方新行: 左与基座对齐
        sx0 = bx0; sy0 = by1 + GAP; sx1 = W; sy1 = H;
      } else {
        // 板满(极罕见): 退化基座+新迹联合归一, 保证内容可见
        return normalizePenItems(baseStrokes.map(function(pts) { return { src: 'pen', pts: pts }; }).concat(sessionItems), W, H);
      }
    }
    var rw = sx1 - sx0, rh = sy1 - sy0;
    var scale = Math.min(rw / (sw || 1), rh / (sh || 1));
    var offX = sx0 - minX * scale + (centered ? (rw - sw * scale) / 2 : 0);
    var offY = sy0 - minY * scale + (centered ? (rh - sh * scale) / 2 : 0);
    return sessionItems.map(function(it) {
      return it.pts.map(function(p) {
        return [Math.round(p[0] * scale + offX), Math.round(p[1] * scale + offY)];
      });
    });
  }
  // 教师演示笔迹色(2026-10-29): 教师端演示卡(__TEACHER_DEMO__ 标记, 组装器 teacherDemo 参数注入)
  // 上老师本会话手写的新笔迹一律红色, 与学生回显的黑色旧迹(含重开载入的基座)一眼区分;
  // 学生端无标记恒黑, 渲染行为零变化
  var DEMO_STROKE_COLOR = '#e53e3e';
  var IS_TEACHER_DEMO = !!window.__TEACHER_DEMO__;
  function __fhwDrawItems(cvs, items, W, H, cssW, cssH) {
    var strokes = [];
    var colors = [];      // 与 strokes 平行: 老师演示笔迹=红, 学生旧迹=null(默认黑)
    var sessionPen = [];
    var sessionPenDemo = false;
    items.forEach(function(it) {
      if (it.src === 'pen') { sessionPen.push(it); if (it.demo) sessionPenDemo = true; }
      else { strokes.push(it.pts); colors.push(it.demo ? DEMO_STROKE_COLOR : null); }
    });
    if (sessionPen.length) {
      var base = items.filter(function(it) { return it.src === 'penbase'; }).map(function(it) { return it.pts; });
      var laid = layoutPenSessionStrokes(base, sessionPen, W, H);
      laid.forEach(function(pts) { strokes.push(pts); colors.push(sessionPenDemo ? DEMO_STROKE_COLOR : null); });
    }
    drawFormulaStrokes(cvs, { w: W, h: H, strokes: strokes, colors: colors }, cssW, cssH);
  }
  function openFormulaPopup(targetEl) {
    var old = document.querySelector('.formula-popup-overlay');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    __stopPenChannel();
    __detachPenListener();
    // 关闭即自动插入(2026-10-23): 弹窗任意关闭路径(✕/灰区)都把手写板当前笔迹落盘到输入框,
    // 无「✓插入/取消」按钮; 无笔迹且原有手写=用户清空过 → 删除手写回到键盘可输入态(清空重写+关闭=擦除)
    var applyStrokesOnClose = function() {
      // 落盘: touch/penbase原样(HW空间), pen会话按基座右侧/下方追加布局转换(不与基座联合归一)
      // colors 平行数组仅教师演示卡存在(老师红笔区分学生黑迹); 学生数据结构与现状一致(无 colors 字段)
      var finalStrokes = [];
      var finalColors = [];
      var hasDemoStroke = false;
      var sessionPen = [];
      items.forEach(function(it) {
        if (it.src === 'pen') sessionPen.push(it);
        else {
          finalStrokes.push(it.pts);
          if (it.demo) hasDemoStroke = true;
          finalColors.push(it.demo ? DEMO_STROKE_COLOR : null);
        }
      });
      if (sessionPen.length) {
        var base = items.filter(function(it) { return it.src === 'penbase'; }).map(function(it) { return it.pts; });
        var laid = layoutPenSessionStrokes(base, sessionPen, HW_W, HW_H);
        var penDemo = sessionPen.some(function(pi) { return pi.demo; });
        if (penDemo) hasDemoStroke = true;
        laid.forEach(function(pts) { finalStrokes.push(pts); finalColors.push(penDemo ? DEMO_STROKE_COLOR : null); });
      }
      var finalSrc = items.some(function(it) { return it.src === 'pen' || it.src === 'penbase'; }) ? 'pen' : 'touch';
      if (finalStrokes.length) {
        // 2026-09-16 只存点数据不再导出 base64 图：批改侧按后端统一 dots 格式绘制识别;
        // src记本源(单一来源约束下恒为同源): 智笔写的重开时按penbase基座载入续写, 提交导出只取w/h/dots不受影响
        // 手写/键盘互斥(2026-11-03 用户拍板·双向确认后清理)：
        //   ①写了笔迹+有旧文字 → confirm「手写将替换已识别文字」→ 是=清文字进手写态, 否=丢弃新笔迹保留旧文字
        //   ②无旧文字 → 直接进手写态(无用户态数据需清理, 不需确认)
        //   订正场景实测: 第一次笔写"齐头并进"识别回填, 订正重写"5678"但旧文字不清→提交仍是旧答案
        var hadOldText = targetEl.value && targetEl.value.trim();
        if (hadOldText) {
          var doReplace = window.confirm('您已有识别文字「' + hadOldText.slice(0, 20) + '」，用手写笔迹替换将清除该文字。确定替换吗？');
          if (!doReplace) {
            // 丢弃新笔迹, 保留旧文字——不开手写态, 不写 __formulaHandwriting
            return;
          }
        }
        targetEl.__formulaHandwriting = { w: HW_W, h: HW_H, strokes: finalStrokes, src: items[0].src };
        if (hasDemoStroke) targetEl.__formulaHandwriting.colors = finalColors;
        targetEl.readOnly = true;
        targetEl.value = '';
        targetEl.placeholder = '已手写（点缩略图可重写）';
        renderFormulaHwPreview(targetEl);
      } else if (targetEl.__formulaHandwriting) {
        delete targetEl.__formulaHandwriting;
        if (targetEl.readOnly) {
          // 手写锁定态清笔迹退回键盘: 清标记; 文本态(value=键盘文本/识别结果)保留, 只移除笔迹证据
          targetEl.readOnly = false;
          targetEl.value = '';
          targetEl.placeholder = '';
        }
        var pv = document.querySelector('.formula-hw-preview[data-for="' + targetEl.id + '"]');
        if (pv && pv.parentNode) pv.parentNode.removeChild(pv);
      }
    };
    var closePopup = function() {
      __rpStop(false);   // 关板终止播放动画(不重绘, 板即将销毁)
      __stopPenChannel();
      __detachPenListener();
      applyStrokesOnClose();
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    };
    var overlay = document.createElement('div');
    overlay.className = 'formula-popup-overlay';
    overlay.style.cssText = 'position:fixed;left:0;top:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:10000;display:flex;align-items:center;justify-content:center;';
    overlay.addEventListener('click', function(e) { if (e.target === overlay) closePopup(); });
    var panel = document.createElement('div');
    // 面板宽度贴合画布（画布+padding），上限 94vw——续写时画布等比缩小面板跟着收窄，不留右侧空白
    // 面板宽度兜底: 续写等比缩小后画布窄于按钮行(5按钮约450px)会溢出边框, 取下限480px(max-width仍兜小屏)
    panel.style.cssText = 'background:#fff;border-radius:14px;padding:14px;width:' + Math.max(dispW + 28, 480) + 'px;max-width:94vw;max-height:92vh;display:flex;flex-direction:column;box-shadow:0 12px 40px rgba(0,0,0,0.35);position:relative;';
    // 可用书写区（宽=min(94vw,1400px)-面板padding，高=92vh-工具行预留）。
    // 逻辑书写空间不固定 900×560：空板按可用区实际比例开（画布铺满面板，横向空间利用起来，
    // w/h 随笔迹数据存储、显示端等比渲染不变形）；已有笔迹沿用原 w/h（续写坐标一致），等比缩放显示。
    // 必须显式 px 定宽高——width:100%+max-height+object-fit:contain 组合下位图 letterbox 留白，
    // getBoundingClientRect 的盒≠位图显示区，指针坐标换算错位（笔迹偏移根因）
    var PANEL_AVAIL_W = Math.min(Math.round(window.innerWidth * 0.94), 1400) - 16;
    // 板高上限 640(2026-10-29): 撑开式卡片 iframe(教师端批改卡)的 innerHeight=文档高(上万px),
    // 原式开出的空板高可达 8456px 畸形; 正常视口下 92vh 亦极少超过 640, cap 无副作用
    var PANEL_AVAIL_H = Math.min(Math.round(window.innerHeight * 0.92) - 110, 640);
    var preHw = targetEl.__formulaHandwriting;
    var hasPre = !!(preHw && preHw.strokes && preHw.strokes.length);
    var HW_W = hasPre ? preHw.w : PANEL_AVAIL_W;
    var HW_H = hasPre ? preHw.h : PANEL_AVAIL_H;
    var fitScale = Math.min(PANEL_AVAIL_W / HW_W, PANEL_AVAIL_H / HW_H);
    var dispW = Math.max(240, Math.floor(HW_W * fitScale));
    var dispH = Math.max(150, Math.floor(HW_H * fitScale));
    var cvs = document.createElement('canvas');
    cvs.style.cssText = 'border:1px dashed #b0b7c3;border-radius:8px;background:#fafbfc;touch-action:none;display:block;margin:0 auto;cursor:crosshair;';
    var items = [];
    // 续写载入旧笔迹: 智笔源以penbase挂载(HW空间恒定基座, 续写只追加不重算), touch源/无标记旧数据按touch;
    // colors 携带的红笔迹(老师演示)重开时按 demo 标记恢复——红/黑各归各位, 再续写仍按本会话身份取色
    var loadSrc = preHw && preHw.src === 'pen' ? 'penbase' : 'touch';
    if (hasPre) items = preHw.strokes.map(function(s, idx) {
      return { src: loadSrc, pts: s.slice(), demo: !!(preHw.colors && preHw.colors[idx]) };
    });
    var redrawCvs = function() {
      // cssW/cssH 传真实显示尺寸：drawFormulaStrokes 内统一设置 style 宽高（盒=位图显示区，坐标换算精确）
      __fhwDrawItems(cvs, items, HW_W, HW_H, dispW, dispH);
    };
    redrawCvs();
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button'; closeBtn.textContent = '✕';
    closeBtn.title = '关闭手写板';
    closeBtn.style.cssText = 'position:absolute;top:10px;right:10px;width:32px;height:32px;font-size:16px;line-height:32px;text-align:center;border:none;background:#f0f2f5;color:#606266;border-radius:50%;cursor:pointer;z-index:2;transition:background .15s;';
    closeBtn.addEventListener('mouseenter', function() { closeBtn.style.background = '#e2e6eb'; });
    closeBtn.addEventListener('mouseleave', function() { closeBtn.style.background = '#f0f2f5'; });
    closeBtn.addEventListener('click', closePopup);
    panel.appendChild(closeBtn);
    var hint = document.createElement('div');
    hint.textContent = items.length ? '已载入之前的手写内容，可继续书写，关闭即自动保存' : '请在白色区域手写输入（支持手指/触控笔/鼠标），写完关闭即自动保存';
    hint.style.cssText = 'font-size:13px;color:#5a6472;background:#f5f7fa;border-radius:8px;padding:9px 12px;margin:10px 44px 0 0;display:flex;align-items:center;gap:6px;line-height:1.5;';
    var btnRow = document.createElement('div');
    btnRow.style.cssText = 'margin-top:10px;display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;';
    var leftBar = document.createElement('div');
    leftBar.style.cssText = 'display:flex;gap:8px;';
    // 撤销=逐笔删除板上最后一笔(含历史基座笔): 作文修改手段——错字撤掉重写、撤掉腾出续写空间
    var undoBtn = document.createElement('button');
    undoBtn.type = 'button'; undoBtn.textContent = '↶ 撤销';
    undoBtn.style.cssText = FH_BTN_CSS;
    undoBtn.addEventListener('click', function() { __rpStop(false); if (items.length) { items.pop(); redrawCvs(); } });
    var clearBtn = document.createElement('button');
    clearBtn.type = 'button'; clearBtn.textContent = '清空重写';
    clearBtn.style.cssText = FH_BTN_CSS;
    clearBtn.addEventListener('click', function() { __rpStop(false); items = []; redrawCvs(); });
    var keyboardBtn = document.createElement('button');
    keyboardBtn.type = 'button';
    keyboardBtn.textContent = '⌨ 键盘';
    keyboardBtn.style.cssText = FH_BTN_CSS;
    keyboardBtn.addEventListener('click', function() {
      items = []; // 回键盘=丢弃板上笔迹, 防closePopup自动落盘又把手写写回去
      delete targetEl.__formulaHandwriting;
      if (targetEl.readOnly) {
        // 手写锁定态: 释放键盘并清标记; 文本态(识别/键盘文本)只清笔迹证据, 文本保留(要清文本键盘直删)
        targetEl.readOnly = false;
        targetEl.value = '';
        targetEl.placeholder = '';
      }
      var pv = document.querySelector('.formula-hw-preview[data-for="' + targetEl.id + '"]');
      if (pv && pv.parentNode) pv.parentNode.removeChild(pv);
      closePopup();
    });
    // 三种输入方式(2026-10-23): 键盘/触屏/智能笔; 触屏与智能笔互斥单选——
    // 智能笔点击即连通道收笔迹(无停止双态), 切回触屏/关弹窗自动断开;
    // 笔迹单一来源: 两方式数据不混在一起, 切换即清掉另一来源板上笔迹;
    // 高亮与工具箱ToolkitPanel同方式: 按钮统一基础样式, 激活项加蓝色outline描边+加粗;
    // 输入方式记忆localStorage: 下次打开手写板默认恢复用户习惯方式, 免每次手动切换
    var FH_MODE_KEY = 'zm_handwrite_input_mode';
    var FH_MODE_ACTIVE_OUTLINE = '2px solid #409EFF';
    var touchBtn = document.createElement('button');
    touchBtn.type = 'button';
    touchBtn.textContent = '✍ 触屏';
    touchBtn.style.cssText = FH_BTN_CSS + 'outline:' + FH_MODE_ACTIVE_OUTLINE + ';outline-offset:1px;';
    var penBtn = document.createElement('button');
    penBtn.type = 'button';
    penBtn.textContent = '✒ 智笔';
    penBtn.style.cssText = FH_BTN_CSS;
    var setPenModeStyles = function(penOn) {
      penBtn.style.outline = penOn ? FH_MODE_ACTIVE_OUTLINE : 'none';
      touchBtn.style.outline = penOn ? 'none' : FH_MODE_ACTIVE_OUTLINE;
    };
    // 单一来源清除: 进智笔清触屏笔迹; 切触屏清智笔笔迹(penbase基座+pen会话)
    var clearTouchBoard = function() {
      if (items.length && items[0].src === 'touch') { items = []; redrawCvs(); }
    };
    var clearPenBoard = function() {
      if (items.some(function(it) { return it.src === 'pen' || it.src === 'penbase'; })) { items = []; redrawCvs(); }
    };
    var startPenChannel = function() {
      // 只连通道+高亮, 不清板——自动恢复/显式点击共用; 板上异源笔迹留到首笔pen_stroke惰性清
      // (打开即清会把载入的旧迹/证据笔迹静默抹掉, 关闭走擦除分支=丢数据)
      if (__fhwPenActive) return;
      __fhwPenActive = true;
      setPenModeStyles(true);
      hint.textContent = '正在连接智能笔…';
      window.parent.postMessage({ type: 'pen_write_start' }, window.parent.location.origin);
    };
    touchBtn.addEventListener('click', function() {
      __rpStop(false);
      localStorage.setItem(FH_MODE_KEY, 'touch');
      __stopPenChannel();
      clearPenBoard();
      setPenModeStyles(false);
      hint.textContent = '触屏输入：请在白色区域书写（支持手指/触控笔/鼠标）';
    });
    penBtn.addEventListener('click', function() {
      __rpStop(false);
      localStorage.setItem(FH_MODE_KEY, 'pen');
      clearTouchBoard();   // 显式切换=清另一源(明确重写意图)
      startPenChannel();
    });
    // 左组=输入方式(键盘/触屏/智笔, 先选方式), 右组=编辑操作(撤销/清空)——操作顺序从左到右
    leftBar.appendChild(keyboardBtn);
    leftBar.appendChild(touchBtn);
    leftBar.appendChild(penBtn);
    var rightBar = document.createElement('div');
    rightBar.style.cssText = 'display:flex;gap:8px;';
    // ===== 学生笔迹播放(教师端演示卡 2026-10-29): 按书写顺序逐笔重现学生手写过程 =====
    // 速度=教师端全局"回放速度"设置(localStorage getSpeed, 毫秒——与纸质题回放同一设置源,
    // srcdoc 卡片与教师端同源共享 localStorage, 每次播放实时读, 改设置下次播放即生效),
    // 语义: speed 毫秒=一笔的播放时长(笔内沿学生原始点序匀速), 笔间停顿120ms, 兜底800与纸质题一致;
    // 播放纯动画不动数据, 播完恢复板当前完整状态(学生黑+老师红); 播放对象=板上学生笔迹(排除老师红迹)
    var __rpState = null;
    var replayBtn = null;
    function __rpSpeed() {
      // 教师端 Home 设置写入恒为合法 JSON 数字(见 store getSpeed mutation); 坏值直接抛错显式暴露不兜底
      var v = JSON.parse(localStorage.getItem('getSpeed')) || 800;
      return v < 50 ? 50 : v;
    }
    function __rpDrawUpTo(si, pi) {
      var dpr = window.devicePixelRatio || 1;
      var ctx = cvs.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, HW_W, HW_H);
      ctx.strokeStyle = '#1f2d3d'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      for (var i = 0; i <= si && i < __rpState.strokes.length; i++) {
        var pts = __rpState.strokes[i];
        var upto = (i === si) ? pi : pts.length;
        if (upto <= 0) continue;
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        for (var k = 1; k < upto; k++) ctx.lineTo(pts[k][0], pts[k][1]);
        ctx.stroke();
      }
    }
    function __rpStop(restore) {
      if (__rpState && __rpState.raf) cancelAnimationFrame(__rpState.raf);
      __rpState = null;
      if (replayBtn) replayBtn.textContent = '▶ 播放';
      if (restore) redrawCvs();
    }
    function __rpFrame(now) {
      var st = __rpState;
      if (!st) return;
      if (st.paused) { st.raf = requestAnimationFrame(__rpFrame); return; }
      var perStroke = __rpSpeed();
      var GAP = 120;
      var total = st.strokes.length * (perStroke + GAP);
      // 断点续播: elapsed=已播累计+本段耗时, 暂停切换时把本段累计进 played(见 __rpToggle)
      var elapsed = st.played + (now - st.frameStart);
      if (elapsed >= total) {
        var last = st.strokes[st.strokes.length - 1];
        __rpDrawUpTo(st.strokes.length - 1, last.length);
        hint.textContent = '播放完毕（点击"▶ 播放"可重播）';
        // 立即收尾清state(不留延时窗口——窗口期内点按钮会走暂停分支而帧循环已死, 按钮卡"▶ 继续");
        // 播完最后一帧已是完整笔迹, __rpStop(true) 的 redrawCvs 画面等价无跳变
        __rpStop(true);
        return;
      }
      var idx = Math.floor(elapsed / (perStroke + GAP));
      var inStroke = elapsed - idx * (perStroke + GAP);
      var pts = st.strokes[idx];
      var pi = Math.min(pts.length, Math.floor(inStroke / perStroke * pts.length) + 1);
      __rpDrawUpTo(idx, pi);
      st.raf = requestAnimationFrame(__rpFrame);
    }
    function __rpToggle() {
      if (__rpState) {
        // 播放中点击=暂停/继续; 暂停定格时把本段耗时累计进 played, 继续时重开一段时钟——断点续播
        var st = __rpState;
        if (!st.paused) { st.played = st.played + (performance.now() - st.frameStart); }
        st.paused = !st.paused;
        st.frameStart = performance.now();
        replayBtn.textContent = st.paused ? '▶ 继续' : '⏸ 暂停';
        return;
      }
      var studentItems = items.filter(function(it) { return !it.demo; });
      if (!studentItems.length) { hint.textContent = '板上无学生笔迹可播放'; return; }
      __rpState = { strokes: studentItems.map(function(it) { return it.pts; }), played: 0, frameStart: performance.now(), paused: false, raf: 0 };
      replayBtn.textContent = '⏸ 暂停';
      hint.textContent = '正在播放学生书写过程（速度=首页·更多·回放速度设置）';
      __rpState.raf = requestAnimationFrame(__rpFrame);
    }
    if (IS_TEACHER_DEMO) {
      replayBtn = document.createElement('button');
      replayBtn.type = 'button';
      replayBtn.textContent = '▶ 播放';
      replayBtn.title = '按书写顺序播放学生笔迹（速度=首页回放速度设置）';
      replayBtn.style.cssText = FH_BTN_CSS;
      replayBtn.addEventListener('click', __rpToggle);
      rightBar.appendChild(replayBtn);
    }
    rightBar.appendChild(undoBtn);
    rightBar.appendChild(clearBtn);
    btnRow.appendChild(leftBar);
    btnRow.appendChild(rightBar);
    panel.appendChild(cvs);
    panel.appendChild(hint);
    panel.appendChild(btnRow);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    // 长文档卡片定位修复(2026-10-29): 教师端批改卡 iframe 是"撑开式"(frameElement 高=文档高,
    // 宿主按内容撑开不滚动iframe)——fixed 居中=挂到文档几何中心(实测 top≈4816/文档10110),
    // 用户视口对应文档顶部只见灰蒙层不见板和笔迹。撑开式卡片改为绝对定位到输入框正下方;
    // 独立滚动iframe(正常题目页)维持原全屏居中形态
    var __fe = null;
    try { __fe = window.frameElement; } catch (e) { __fe = null; }
    var __docH2 = document.documentElement.scrollHeight;
    var __stretched = __fe && __fe.offsetHeight >= __docH2 - 80;
    if (__stretched) {
      overlay.style.alignItems = 'flex-start';
      var __elTop = targetEl.getBoundingClientRect().top + window.scrollY;
      var __maxTop = Math.max(10, __docH2 - panel.offsetHeight - 20);
      panel.style.position = 'absolute';
      panel.style.left = '50%';
      panel.style.transform = 'translateX(-50%)';
      panel.style.top = Math.min(__elTop + 42, __maxTop) + 'px';
    }
    // 智能笔通道消息: ready/error更新提示, stroke按笔追加(服务端dot_type=2聚合=一笔一条消息)
    __fhwPenListener = function(e) {
      var d = e.data || {};
      if (d.type === 'pen_write_ready') {
        if (__fhwPenActive) hint.textContent = '已连接智能笔 ' + (d.mac || '') + '，请用智能笔在纸上书写';
      } else if (d.type === 'pen_write_error') {
        if (__fhwPenActive) {
          __fhwPenActive = false;
          setPenModeStyles(false);
        }
        hint.textContent = d.message || '智能笔连接失败';
      } else if (d.type === 'pen_stroke') {
        if (!__fhwPenActive) return;
        // 惰性清板: 首笔智笔数据到来(学生真在写)才清载入的触屏笔迹——单一来源, 但不因"打开+自动恢复"误清
        if (items.length && items[0].src === 'touch') { items = []; }
        var data = d.data || {};
        (data.Stroke || []).forEach(function(st) {
          var xs = st.x || [], ys = st.y || [], fp = st.fly_point || [];
          var pts = [];
          for (var i = 0; i < xs.length; i++) {
            // 飞点剔除(服务端show_fly=0默认已滤, 前端防御性双保险)
            if (fp[i] === 1) continue;
            if (xs[i] == null || ys[i] == null) continue;
            pts.push([xs[i], ys[i]]);
          }
          if (pts.length) items.push({ src: 'pen', pts: pts, demo: IS_TEACHER_DEMO });
        });
        redrawCvs();
      }
    };
    window.addEventListener('message', __fhwPenListener);
    // 打开即恢复用户习惯输入方式(不清板: 连通道即可, 首笔数据到来才惰性清异源);
    // pen_write_error时保留记忆不改——习惯仍是智能笔, hint提示未绑定, 绑笔后下次打开即直连
    if (localStorage.getItem(FH_MODE_KEY) === 'pen') {
      startPenChannel();
    }
    // 手写采集：坐标归一到 HW_W×HW_H 逻辑空间，整数存储。
    // 单一来源守卫: 智笔模式下或板上已是智笔笔迹时手指不写板(防误触清板/防混源),
    // 要改用触屏先点「✍ 触屏」(会清掉智笔笔迹)
    var touchBlocked = function() {
      return __fhwPenActive || items.some(function(it) { return it.src === 'pen' || it.src === 'penbase'; });
    };
    cvs.addEventListener('pointerdown', function(ev) {
      ev.preventDefault();
      if (__rpState) return;   // 播放动画进行中不接受书写
      if (touchBlocked()) return;
      cvs.setPointerCapture(ev.pointerId);
      var r = cvs.getBoundingClientRect();
      var p = [Math.round((ev.clientX - r.left) * HW_W / r.width), Math.round((ev.clientY - r.top) * HW_H / r.height)];
      items.push({ src: 'touch', pts: [p], demo: IS_TEACHER_DEMO });
      redrawCvs();
    });
    cvs.addEventListener('pointermove', function(ev) {
      if (touchBlocked()) return;
      if (!items.length || !cvs.hasPointerCapture || !cvs.hasPointerCapture(ev.pointerId)) return;
      ev.preventDefault();
      var r = cvs.getBoundingClientRect();
      var p = [Math.round((ev.clientX - r.left) * HW_W / r.width), Math.round((ev.clientY - r.top) * HW_H / r.height)];
      var last = items[items.length - 1];
      if (last.src !== 'touch') return; // 极端时序: pointerdown后pen_stroke先行入列, 触点不混入笔笔画
      var prev = last.pts[last.pts.length - 1];
      if (Math.abs(p[0] - prev[0]) + Math.abs(p[1] - prev[1]) >= 2) { last.pts.push(p); redrawCvs(); }
    });
  }
  // 公式输入增强：input/textarea 保留原生（支持中文输入），统一附加 ✍ 手写输入按钮
  // 2026-10-28 瘦身：按钮去「手写输入」文字改纯图标（图表/填空题内按钮文字挤压输入区影响
  // 排版），语义保留在 title 悬停提示；padding 收窄为方形小圆钮
  function enhanceTextInputs(q) {
    q.querySelectorAll('input[type="text"], input:not([type]), textarea').forEach(function(el) {
      if (el.getAttribute('data-formula-done')) return;
      if (el.closest && el.closest('[data-voice-fallback]')) return; // 跳过语音降级输入框
      el.setAttribute('data-formula-done', 'true');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'formula-insert-btn';
      btn.setAttribute('data-ro-allow', '1');   // 只读豁免(2026-10-29): ✍手写是教师端批改卡查看学生笔迹的唯一入口
      btn.textContent = '✍';
      btn.title = '手写输入';
      btn.style.cssText = 'box-sizing:border-box;margin-left:4px;padding:5px 8px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #17376f;border-radius:999px;background:#17376f;color:#fff;font-size:13px;font-weight:600;line-height:1;cursor:pointer;vertical-align:middle;';
      el.parentNode.insertBefore(btn, el.nextSibling);
      btn.addEventListener('click', function() { openFormulaPopup(el); });
      // 键盘输入→清除笔迹(2026-11-03 用户拍板·双向互斥+确认后清理)：输入框有手写笔迹且非readOnly
      // (订正回显的识别文本态——value非空+有strokes)时学生打字=明确要换键盘作答, confirm 后清笔迹
      el.addEventListener('input', function() {
        if (!el.__formulaHandwriting || !el.__formulaHandwriting.strokes || !el.__formulaHandwriting.strokes.length) return;
        var doClear = window.confirm('该空已有手写笔迹，键盘输入将清除手写内容。确定切换为键盘输入吗？');
        if (!doClear) {
          // 撤销本次输入, 恢复旧文字, 保留笔迹
          el.value = el.getAttribute('data-pre-hw-text') || '';
          return;
        }
        delete el.__formulaHandwriting;
        var pv = document.querySelector('.formula-hw-preview[data-for="' + el.id + '"]');
        if (pv && pv.parentNode) pv.parentNode.removeChild(pv);
      });
      // 记录打字前的值(供撤销恢复)
      el.addEventListener('focus', function() {
        el.setAttribute('data-pre-hw-text', el.value);
      });
    });
  }
  function initJudge(q) {
    q.querySelectorAll('.judge-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        q.querySelectorAll('.judge-btn').forEach(function(b) { b.classList.remove('selected'); });
        btn.classList.add('selected');
      });
    });
  }

  // ====== 6b. 口语表达语音识别（voice_speak 共用）—— SpeechRecognition 识别 ======
  var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  var currentVoiceRecognition = null;
  var currentVoiceBtn = null;

  function resetVoiceBtn(btn) {
    var icon = btn.querySelector('.voice-icon');
    var text = btn.querySelector('.voice-text');
    if (icon) icon.textContent = '\\uD83C\\uDFA4';
    if (text) text.textContent = '点击朗读';
  }

  // ====== 6d. 口语表达引擎（voice_speak）—— SpeechRecognition 识别 + 后端LLM批改 ======
  function initVoiceSpeak(q) {
    var voiceBtn = q.querySelector('.voice-btn');
    if (!voiceBtn) return;
    var origBtnText = (voiceBtn.querySelector('.voice-text') || voiceBtn).textContent;   // 静音拦截复位用（口语/跟读契约文案不同，不硬编码）
    var recognizedArea = q.querySelector('[data-voice-recognized]');
    var setBtnText = function(t) {
      var vt = voiceBtn.querySelector('.voice-text');
      if (vt) vt.textContent = t; else voiceBtn.textContent = t;
    };
    var say = function(t) { if (recognizedArea) recognizedArea.textContent = t; };
    // 口语表达：getUserMedia 本地录音 + 上传 OSS，批改侧再做语音识别/大模型批改。
    // 不用 Web Speech API——它依赖谷歌在线服务，国内网络不可用（network 错误）。
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      // 口语表达必须录音作答(2026-10-31 用户拍板): 评测走腾讯SOE看发音, 打字评不了——
      // 打字兜底(data-voice-fallback)废弃, 不支持录音时明确提示换浏览器, 不再提供文字作答
      voiceBtn.disabled = true;
      setBtnText('当前浏览器不支持录音');
      say('当前浏览器不支持录音——本题需要录音作答（评测发音），请换用支持麦克风的浏览器（Chrome/Edge）');
      return;
    }
    var recording = false;
    var audioCtx = null, source = null, processor = null, stream = null, chunks = [];
    voiceBtn.addEventListener('click', function() {
      if (voiceBtn.disabled) return;
      if (!recording) {
        navigator.mediaDevices.getUserMedia({ audio: true }).then(function(s) {
          stream = s;
          audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
          source = audioCtx.createMediaStreamSource(stream);
          processor = audioCtx.createScriptProcessor(4096, 1, 1);
          chunks = [];
          processor.onaudioprocess = function(e) { chunks.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
          source.connect(processor);
          processor.connect(audioCtx.destination);
          recording = true;
          setBtnText('■ 点击停止录音');
          say('🔴 正在录音...说完后再点一次按钮结束');
        }).catch(function(err) {
          var name = (err && err.name) || '';
          if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
            say('⚠ 麦克风权限被拒绝：请点击浏览器地址栏左侧的🔒/🎤图标 → 麦克风 → 允许，然后重试（本题需录音作答，评测发音）');
          } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
            say('未检测到麦克风设备——本题需录音作答（评测发音），请连接麦克风后重试');
          } else {
            say('录音启动失败(' + (name || '未知') + ')，请重试（本题需录音作答，评测发音）');
          }
        });
      } else {
        processor.disconnect();
        source.disconnect();
        stream.getTracks().forEach(function(t) { t.stop(); });
        var actualRate = audioCtx.sampleRate || 16000;   // close 前读实际采样率(移动端可能≠16k)
        audioCtx.close();
        recording = false;
        // 静音检测(2026-11-02 修复实测qid115717 attempt2): 全零录音(麦克风被占用/系统静音/没出声)
        // 原样上传→SOE评0分+订正白耗一次机会——峰值<0.01(≈16bit的328, 远低于正常环境底噪)拦截,
        // 提示检查麦克风重录, 不发上传(显式暴露不静默)
        var merged = mergeBuffers(chunks);
        var peak = 0;
        for (var pi = 0; pi < merged.length; pi++) { var av = Math.abs(merged[pi]); if (av > peak) peak = av; }
        if (peak < 0.01) {
          chunks = [];
          setBtnText(origBtnText);
          say('⚠ 未检测到声音（麦克风可能被占用或静音）——请检查麦克风后点击重新录音，本次录音未上传');
          return;
        }
        setBtnText('⏳ 上传中...');
        say('录音完成，正在上传...');
        // 采样率归一(2026-10-31 跨平台加固): 实际≠16k(浏览器忽略构造参数)时重采样, SOE评测要求16k
        var pcm = resampleBuffer(merged, actualRate, 16000);
        var wav = encodeWAV(floatTo16BitPCM(pcm), 16000);
        var base64 = arrayBufferToBase64(wav);
        // subId(2026-11-02 子空作用域修复): 复合页语音空=空容器[data-sub-id], 单题形态=题容器(空串)——
        // 上传/回执全链路带 subId, 回执才能把 data-voice-audio-url 写到"收集器要读的容器"上
        // (原恒写题容器, 复合页空容器读不到 → 提交载荷 answer:"" params:{}, 实测 qid115710)。
        // questionId 必须 getQuestion(q) 取题容器的——复合页 q=空容器无此属性, 直读发 null 出去,
        // 宿主回执 questionId:null 谁都匹配不上 → 按钮永远停在"上传中"(实测 20261102 复测)
        window.parent.postMessage({ type: 'upload_voice_audio', questionId: (getQuestion(q) || q).getAttribute('data-question-id'), subId: q.getAttribute('data-sub-id') || '', audioBase64: base64 }, window.parent.location.origin);
      }
    });
    // 上传回执（voice_read 共用的监听只写 voice-status，这里同步写口语题的识别区）
    window.addEventListener('message', function(e) {
      var d = e.data || {};
      if (d.type !== 'voice_audio_uploaded') return;
      // 复合页 q=空容器无 data-question-id（原判断在此恒不匹配=口语空按钮文案/识别区从不更新），向上找题容器
      if (!getQuestion(q) || d.questionId !== getQuestion(q).getAttribute('data-question-id')) return;
      if (String(d.subId || '') !== (q.getAttribute('data-sub-id') || '')) return;   // 同题多语音空互不串扰
      if (d.success && d.audioUrl) {
        setBtnText('✔ 录音完成');
        say('✅ 录音已提交（提交答案后由系统批改），也可以再录一次覆盖');
      } else {
        setBtnText('🎤 点击开始录音');
        say('上传失败，请重试（本题需录音作答，评测发音）');
      }
    });
  }
  // ====== 6f. 跟读朗读引擎（voice_read）—— getUserMedia 录音 + 上传 OSS + 讯飞ISE评测 ======
  function mergeBuffers(bufferArray) {
    var len = 0;
    for (var i = 0; i < bufferArray.length; i++) len += bufferArray[i].length;
    var result = new Float32Array(len);
    var offset = 0;
    for (var j = 0; j < bufferArray.length; j++) { result.set(bufferArray[j], offset); offset += bufferArray[j].length; }
    return result;
  }
  // 采样率归一(2026-10-31 跨平台加固): 部分安卓/iOS浏览器忽略 AudioContext({sampleRate:16000})
  // 参数按硬件 44100/48000 跑——WAV 头硬编码16k会回放变速, 且腾讯SOE引擎(16k_zh/16k_en)要求
  // 16kHz PCM, 采样率错位评测失败/失真; 线性插值重采样到目标采样率
  function resampleBuffer(input, fromRate, toRate) {
    if (!input || !input.length || fromRate === toRate) return input;
    var ratio = fromRate / toRate;
    var outLen = Math.floor(input.length / ratio);
    var out = new Float32Array(outLen);
    for (var i = 0; i < outLen; i++) {
      var srcPos = i * ratio;
      var i0 = Math.floor(srcPos);
      var i1 = Math.min(i0 + 1, input.length - 1);
      var frac = srcPos - i0;
      out[i] = input[i0] * (1 - frac) + input[i1] * frac;
    }
    return out;
  }
  function floatTo16BitPCM(input) {
    var output = new DataView(new ArrayBuffer(input.length * 2));
    for (var i = 0; i < input.length; i++) {
      var s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
    return output;
  }
  function encodeWAV(samples, sampleRate) {
    var buffer = new ArrayBuffer(44 + samples.byteLength);
    var view = new DataView(buffer);
    function writeString(offset, str) { for (var i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); }
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + samples.byteLength, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, samples.byteLength, true);
    new Uint8Array(buffer, 44).set(new Uint8Array(samples.buffer));
    return buffer;
  }
  function arrayBufferToBase64(buffer) {
    var bytes = new Uint8Array(buffer);
    var binary = '';
    var chunk = 0x8000;
    for (var i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }
  // ====== 6f. 跟读朗读引擎（voice_read）—— getUserMedia 录音 + 上传 OSS + 腾讯SOE评测 ======
  // 2026-11-02 状态反馈与口语表达(initVoiceSpeak)完全对齐: 原实现只写 .voice-status(退役前老结构,
  // 子题契约根本不生成该元素)且按钮文字从不变——学生点击后无"正在录音/点击停止"任何状态(实测);
  // 现统一走 按钮文字(.voice-text, 缺失回落按钮textContent) + 识别区([data-voice-recognized]) 双通道
  function initVoiceRead(q) {
    var voiceBtn = q.querySelector('.voice-btn');
    if (!voiceBtn) return;
    var origBtnText = (voiceBtn.querySelector('.voice-text') || voiceBtn).textContent;   // 静音/失败复位用
    var recognizedArea = q.querySelector('[data-voice-recognized]');
    var setBtnText = function(t) {
      var vt = voiceBtn.querySelector('.voice-text');
      if (vt) vt.textContent = t; else voiceBtn.textContent = t;
    };
    var say = function(t) { if (recognizedArea) recognizedArea.textContent = t; };
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      voiceBtn.disabled = true;
      setBtnText('当前浏览器不支持录音');
      say('当前浏览器不支持录音——本题需要录音作答（评测发音），请换用支持麦克风的浏览器（Chrome/Edge）');
      return;
    }
    var recording = false;
    var audioCtx = null, source = null, processor = null, stream = null, chunks = [];
    voiceBtn.addEventListener('click', function() {
      if (voiceBtn.disabled) return;
      if (!recording) {
        navigator.mediaDevices.getUserMedia({ audio: true }).then(function(s) {
          stream = s;
          audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
          source = audioCtx.createMediaStreamSource(stream);
          processor = audioCtx.createScriptProcessor(4096, 1, 1);
          chunks = [];
          processor.onaudioprocess = function(e) { chunks.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
          source.connect(processor);
          processor.connect(audioCtx.destination);
          recording = true;
          setBtnText('■ 点击停止录音');
          say('🔴 正在录音...请朗读，再点一次按钮结束');
        }).catch(function(err) {
          var name = (err && err.name) || '';
          if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
            say('⚠ 麦克风权限被拒绝：请点击浏览器地址栏左侧的🔒/🎤图标 → 麦克风 → 允许，然后重试（本题需录音作答，评测发音）');
          } else {
            say('录音启动失败(' + (name || '未知') + ')，请重试（本题需录音作答，评测发音）');
          }
        });
      } else {
        processor.disconnect();
        source.disconnect();
        stream.getTracks().forEach(function(t) { t.stop(); });
        var actualRate = audioCtx.sampleRate || 16000;   // close 前读实际采样率(移动端可能≠16k)
        audioCtx.close();
        recording = false;
        // 静音检测(2026-11-02 同 initVoiceSpeak·实测qid115717 attempt2 全零文件): 峰值<0.01 拦截重录
        var merged = mergeBuffers(chunks);
        var peak = 0;
        for (var pi = 0; pi < merged.length; pi++) { var av = Math.abs(merged[pi]); if (av > peak) peak = av; }
        if (peak < 0.01) {
          chunks = [];
          setBtnText(origBtnText);
          say('⚠ 未检测到声音（麦克风可能被占用或静音）——请检查麦克风后点击重新录音，本次录音未上传');
          return;
        }
        setBtnText('⏳ 上传中...');
        say('录音完成，正在上传...');
        // 采样率归一(2026-10-31 跨平台加固, 同 initVoiceSpeak): SOE评测要求16kHz PCM
        var pcm = resampleBuffer(merged, actualRate, 16000);
        var wav = encodeWAV(floatTo16BitPCM(pcm), 16000);
        var base64 = arrayBufferToBase64(wav);
        // subId 同 initVoiceSpeak(2026-11-02 子空作用域修复): 复合页跟读空回执要落空容器;
        // questionId 同样 getQuestion(q) 取题容器的(空容器直读为 null, 回执匹配不上)
        window.parent.postMessage({ type: 'upload_voice_audio', questionId: (getQuestion(q) || q).getAttribute('data-question-id'), subId: q.getAttribute('data-sub-id') || '', audioBase64: base64 }, window.parent.location.origin);
      }
    });
    // 上传回执（与口语同款双通道状态；getQuestion 取题容器+subId 双匹配防同题多语音空串扰）
    window.addEventListener('message', function(e) {
      var d = e.data || {};
      if (d.type !== 'voice_audio_uploaded') return;
      if (!getQuestion(q) || d.questionId !== getQuestion(q).getAttribute('data-question-id')) return;
      if (String(d.subId || '') !== (q.getAttribute('data-sub-id') || '')) return;
      if (d.success && d.audioUrl) {
        setBtnText('✔ 录音完成');
        say('✅ 录音已提交（提交答案后由系统批改），也可以再录一次覆盖');
      } else {
        setBtnText(origBtnText);
        say('上传失败，请重试（本题需录音作答，评测发音）');
      }
    });
  }
  // 上传回执落位（2026-11-02 子空作用域修复）：data-voice-audio-url 写在"收集器要读的容器"上——
  // 复合页语音空=空容器[data-sub-id]（collectMulti 按空调 collectVoiceSpeak/Read），单题形态=题容器。
  // 原实现恒写题容器，复合页空容器读不到 URL → 提交 answer:"" params:{} 判"无录音0分"、回显无音频（实测 qid115710）。
  // subId 由上传消息发出、宿主(adaptiveLearningPanel)透传回来；声明了 subId 但题内找不到该空=DOM 异常，
  // console.error 暴露不回落题容器（回落写错位，下次收集照样丢）
  window.addEventListener('message', function(e) {
    var d = e.data || {};
    if (d.type !== 'voice_audio_uploaded') return;
    var q = document.querySelector('[data-question-id="' + d.questionId + '"]');
    if (!q) return;
    var target = q, _subId = String(d.subId || '');
    if (_subId) {
      target = null;
      q.querySelectorAll('[data-sub-id]').forEach(function(s) { if (s.getAttribute('data-sub-id') === _subId) target = s; });
      if (!target) { console.error('[question-runtime] voice_audio_uploaded: 题 ' + d.questionId + ' 内不存在空 ' + _subId + '，回执无处落位'); return; }
    }
    var st = target.querySelector('.voice-status');
    if (d.success && d.audioUrl) {
      target.setAttribute('data-voice-audio-url', d.audioUrl);
      if (st) st.textContent = '上传完成，请点击提交答案';
    } else {
      if (st) st.textContent = '上传失败，请重试';
    }
  });

  // ====== 7. 提交收集（各题型，answer 格式与后端对齐）======
  function collectDrag(q) {
    var pairs = [];
    // 全卡收集(2026-11-03 修复)：原 t.querySelector('[data-drag-id]') 只收框内第一张——学生把两张卡
    // 拖进同一框时后一张被静默丢弃，收集不全判分失真(丢的恒判错且无从核对)。改为遍历框内全部卡，
    // 同框多对如实入卷：判分按对核对(pair 数≠正确对数恒 allCorrect=false)，与批改侧同口径；
    // 回显侧 dragPairs 逐对 appendChild 天然兼容同框多卡
    q.querySelectorAll('[data-target-id]').forEach(function(t) {
      t.querySelectorAll('[data-drag-id]').forEach(function(item) {
        pairs.push(item.getAttribute('data-drag-id') + '-' + t.getAttribute('data-target-id'));
      });
    });
    var correctArr = getCorrectArr(q);
    var correctCount = pairs.filter(function(p) { return correctArr.indexOf(p) !== -1; }).length;
    var allCorrect = correctCount === correctArr.length && pairs.length === correctArr.length;
    return { answer: pairs.join(','), correct: allCorrect, score: allCorrect ? 1 : correctCount / Math.max(correctArr.length, 1), params: { dragPairs: pairs.map(function(p) { var parts = p.split('-'); return { dragId: parts[0], targetId: parts[1] || '' }; }) }, reason: allCorrect ? '全部匹配正确' : '正确' + correctCount + '/' + correctArr.length };
  }
  function collectSort(q) {
    var order = [];
    if (q._isSlotMode) {
      q.querySelectorAll('[data-slot]').forEach(function(s) { var card = s.querySelector('[data-sort-id]'); order.push(card ? card.getAttribute('data-sort-id') : ''); });
    } else {
      q.querySelectorAll('[data-sort-id]').forEach(function(el) { order.push(el.getAttribute('data-sort-id')); });
    }
    var correctArr = getCorrectArr(q);
    var allCorrect = order.length === correctArr.length && order.every(function(id, i) { return id === correctArr[i]; });
    return { answer: order.join(','), correct: allCorrect, score: allCorrect ? 1 : 0, params: { studentOrder: order }, reason: allCorrect ? '排序正确' : '部分位置不正确' };
  }
  function collectMatch(q) {
    var pairs = [];
    q.querySelectorAll('[data-match-source]').forEach(function(src) {
      var t = src.getAttribute('data-connected-target');
      if (t) pairs.push(src.getAttribute('data-match-source') + '-' + t);
    });
    var correctArr = getCorrectArr(q);
    var correctSet = {}; correctArr.forEach(function(c) { correctSet[c] = true; });
    var correctCount = pairs.filter(function(p) { return correctSet[p]; }).length;
    var allCorrect = correctCount === correctArr.length && pairs.length === correctArr.length;
    return { answer: pairs.join(','), correct: allCorrect, score: allCorrect ? 1 : correctCount / Math.max(correctArr.length, 1), params: { matchPairs: pairs.map(function(p) { var parts = p.split('-'); return { source: parts[0], target: parts[1] || '' }; }) }, reason: allCorrect ? '全部匹配正确' : '正确' + correctCount + '/' + correctArr.length };
  }
  function collectMemory(q) {
    // answer=已配对的规范对串（如 1-5,2-6，对内小ID在前，与 data-correct-answer/批改侧同格式），
    // 从配对时写入的 data-matched-pair 读取，去重后排序保证稳定；score=对数/总对数（≤1）。
    // 原实现收裸 data-card-group（每对重复 2 次）去比 "ID-ID" 串恒 0 命中、score=卡数/对数可达 2.0
    var seen = {}, pairs = [];
    q.querySelectorAll('[data-card-id].matched[data-matched-pair]').forEach(function(card) {
      var k = card.getAttribute('data-matched-pair');
      if (k && !seen[k]) { seen[k] = true; pairs.push(k); }
    });
    pairs.sort();
    var correctSet = memoryPairSet(q);
    var total = Object.keys(correctSet).length;
    var correctCount = pairs.filter(function(p) { return correctSet[p]; }).length;
    var allCorrect = total > 0 && correctCount === total;
    return { answer: pairs.join(','), correct: allCorrect, score: total ? correctCount / total : 0, params: { matchedPairs: pairs }, reason: allCorrect ? '全部翻对' : '已配对' + correctCount + '/' + total };
  }
  function collectChoice(q) {
    // 一题多空的 choice 空由 collectMulti 按 data-sub-type 分发到本函数（空容器作 scope，内部无嵌套 data-sub-id），
    // 此处只走单空路径；旧「choice 题内嵌多 data-sub-id 子组」形态已标准化为 multi（2026-09-16 双轨分支删除，存量为测试数据不迁移）。
    var sel = q.querySelector('[data-option].selected');
    if (!sel) return null;
    var isCorrect = sel.getAttribute('data-correct') === 'true';
    var reasonArea = q.querySelector('textarea');
    var reasonText = reasonArea ? reasonArea.value.trim() : '';
    return { answer: sel.getAttribute('data-option'), correct: isCorrect, score: isCorrect ? 1 : 0, params: { option: sel.getAttribute('data-option'), isCorrect: isCorrect, studentReason: reasonText }, reason: isCorrect ? '选择正确' : '选择错误' };
  }
  function collectJudge(q) {
    var sel = q.querySelector('.judge-btn.selected');
    if (!sel) return null;
    var answer = sel.getAttribute('data-option');
    var isCorrect = answer === getCorrectAttr(q);
    return { answer: answer, correct: isCorrect, score: isCorrect ? 1 : 0, params: { answer: answer, isCorrect: isCorrect }, reason: isCorrect ? '判断正确' : '判断错误' };
  }
  function collectFill(q) {
    var pairs = [];
    q.querySelectorAll('input[id], textarea[id], select[id]').forEach(function(el) { if (el.id) pairs.push(el.id + ':' + el.value); });
    var correctStr = getCorrectAttr(q);
    var fillParams = { text: pairs.join('|') };
    // 手写空点数据导出（2026-09-16 对齐后端笔批改统一 dots 格式：每笔一轨迹、x/y 分离数组，
    // 后端 handwritten_pic 白底绘制后走统一识别批改；不导出 base64 图——提交体积从几百KB降到几KB）
    var hwDots = {};
    q.querySelectorAll('input[id], textarea[id]').forEach(function(el) {
      var hw = el.__formulaHandwriting;
      if (el.id && hw && hw.strokes && hw.strokes.length) {
        hwDots[el.id] = {
          w: hw.w, h: hw.h,
          dots: hw.strokes.map(function(s) {
            return { x: s.map(function(p) { return p[0]; }), y: s.map(function(p) { return p[1]; }) };
          })
        };
      }
    });
    if (Object.keys(hwDots).length) fillParams.handwrite_dots = hwDots;
    // 开放题识别：标准答案为"略"或空的空无唯一解（语文创作类填空），不参与自动判分。
    // 同时根治空真判分：曾出现生成页 0 个输入框 + data-correct-answer="略"，
    // correctPairs=['略'] 拆出期望值为空串、元素不存在实际值也是空串 → 相等 → 误判"全部正确"满分。
    var exactPairs = [];
    correctStr.split('|').forEach(function(cp) {
      var expected = (cp.split(':')[1] || '').trim();
      if (expected && expected !== '略') exactPairs.push(cp);
    });
    if (!exactPairs.length) {
      return { answer: pairs.join('|'), correct: false, score: 0, params: fillParams, reason: '开放题·待批改' };
    }
    var correctCount = 0;
    exactPairs.forEach(function(cp) {
      var parts = cp.split(':');
      var fid = parts[0]; var expected = parts[1] || '';
      // 普通输入框：优先按 id，回退 data-fill-id（公式空统一为普通输入框+手写，math-field 已退役）
      var el = document.getElementById(fid) || q.querySelector('input[data-fill-id="' + fid + '"], textarea[data-fill-id="' + fid + '"]');
      var actual = el ? (el.value || '') : '';
      if (normalizeLatexAnswer(actual) === normalizeLatexAnswer(expected)) correctCount++;
    });
    var allCorrect = correctCount === exactPairs.length;
    return { answer: pairs.join('|'), correct: allCorrect, score: allCorrect ? 1 : correctCount / exactPairs.length, params: fillParams, reason: allCorrect ? '全部正确' : '正确' + correctCount + '/' + exactPairs.length };
  }
  function collectVoiceSpeak(q) {
    // 口语表达必须录音作答(2026-10-31 用户拍板): 评测走腾讯SOE专门语音评测API看发音,
    // 打字文本评不了发音——data-voice-fallback 打字兜底废弃; 无录音不算有效作答(同跟读)
    var audioUrl = q.getAttribute('data-voice-audio-url');
    if (!audioUrl) return null;
    return { answer: audioUrl, correct: false, score: 0, params: { voiceAudioUrl: audioUrl }, reason: '待批改' };
  }
  function collectVoiceRead(q) {
    var audioUrl = q.getAttribute('data-voice-audio-url');
    if (!audioUrl) return null;
    return { answer: audioUrl, correct: false, score: 0, params: { voiceAudioUrl: audioUrl }, reason: '待批改' };
  }
  function collectReflection(q) {
    var texts = [];
    q.querySelectorAll('textarea').forEach(function(ta) { if (ta.value.trim()) texts.push(ta.value.trim()); });
    var ratings = collectRatings(q);
    if (!texts.length && !Object.keys(ratings).length) return null;
    var answer = texts.join('\\n');
    var params = { reflection_answer: answer };
    if (Object.keys(ratings).length) params.ratings = ratings;
    return { answer: answer, correct: true, score: 1, params: params, reason: '反思提交' };
  }

  // ====== 复合题（multi）：一题多空，每空独立题型，整题一个提交按钮 ======
  // 聚合 answer 格式与后端 extract/批改对齐：subId:value||subId:value（空与空用 || 分隔，
  // 空内保留原题型格式——fill 空的 id:value|id:value、drag/sort 的逗号串不受影响）。
  // 判分：逐空调用该空题型的 COLLECTOR（读空容器 data-correct-answer / 选项 data-correct），
  // 得分=各空得分平均（与填空多空、批改侧口径一致）。
  function collectMulti(q) {
    var subs = q.querySelectorAll('[data-sub-id]');
    if (!subs.length) return null;
    var parts = [], subAnswers = {}, scoreSum = 0, unanswered = 0, graded = 0;
    subs.forEach(function(sub) {
      var subId = sub.getAttribute('data-sub-id');
      var subType = sub.getAttribute('data-sub-type') || '';
      var fn = COLLECTORS[subType];
      if (!fn) return; // 未知空类型跳过该空（生成期 validate 拦截，运行时不炸整题）
      var data = fn(sub);
      var ans = data ? (data.answer || '') : '';
      if (!data || !ans) unanswered++;
      parts.push(subId + ':' + ans);
      // 开放空（标准答案为"略"或空——采集/创作类不判对错）：不计入判分分母
      var stAns = sub.getAttribute('data-correct-answer') || '';
      if (!stAns || stAns === '略') {
        subAnswers[subId] = { type: subType, answer: ans, score: 1, isCorrect: true, open: true, params: (data && data.params) || {} };
        return;
      }
      var sc = data ? (data.score !== undefined ? Number(data.score) || 0 : (data.correct ? 1 : 0)) : 0;
      scoreSum += sc;
      graded++;
      subAnswers[subId] = { type: subType, answer: ans, score: sc, isCorrect: sc >= 1, params: (data && data.params) || {} };
    });
    var n = subs.length;
    if (!parts.length) return null;
    var score = graded ? scoreSum / graded : 1;
    return {
      answer: parts.join('||'),
      correct: score >= 1 && unanswered === 0,
      score: score,
      params: { subAnswers: subAnswers, unanswered: unanswered },
      reason: unanswered ? (n - unanswered) + '/' + n + ' 已作答' : (score >= 1 ? '全部正确' : '部分正确')
    };
  }

  // ====== 星级自评控件（rating-stars）======
  // 后端反思页生成 prompt 承诺"交互 JS 由前端运行时注入"（标准结构 .rating-stars[data-rating-value]
  // 内含 [data-star="1..n"] 子元素），此处兑现：点击第 n 颗星 → 容器 data-rating-value=n、
  // 前 n 颗加 .selected。2026-08-26 学生端实测反思页星星点击无任何反应即此承诺缺口。
  function initRatingStars(q) {
    q.querySelectorAll('.rating-stars').forEach(function(box) {
      var stars = box.querySelectorAll('[data-star]');
      if (!stars.length || box.__ratingBound) return;
      box.__ratingBound = true;
      stars.forEach(function(s) {
        s.addEventListener('click', function(e) {
          e.stopPropagation();
          var n = parseInt(s.getAttribute('data-star'), 10) || 1;
          box.setAttribute('data-rating-value', String(n));
          stars.forEach(function(x) {
            x.classList.toggle('selected', (parseInt(x.getAttribute('data-star'), 10) || 0) <= n);
          });
        });
      });
    });
  }
  function collectRatings(q) {
    var ratings = {};
    q.querySelectorAll('.rating-stars[data-rating-value]').forEach(function(box, i) {
      var v = (box.getAttribute('data-rating-value') || '').trim();
      if (v && v !== '0') ratings[box.id || ('rating_' + (i + 1))] = v;
    });
    return ratings;
  }
  function restoreRatings(q, ratings) {
    if (!ratings) return;
    Object.keys(ratings).forEach(function(key) {
      var box = document.getElementById(key) || q.querySelectorAll('.rating-stars')[parseInt(String(key).replace(/[^0-9]/g, ''), 10) - 1];
      if (!box) return;
      var n = parseInt(ratings[key], 10) || 0;
      box.setAttribute('data-rating-value', String(n));
      box.querySelectorAll('[data-star]').forEach(function(x) {
        x.classList.toggle('selected', (parseInt(x.getAttribute('data-star'), 10) || 0) <= n);
      });
    });
  }
  // ====== 8c. 圈画题（annotate）：直接在文本上划选目标词句（用户拍板：无overlay/canvas，鼠标触摸跟随文本） ======
  // 页面契约（AI 纯HTML产出，交互JS全部由本运行时注入）：
  //   data-question-type="annotate"
  //   data-annotate-answers="词1|词2"   标准答案=原文中真实存在的连续词句(|分隔)；缺省或「略」=开放型
  //                                    (2026-11-05 用户拍板·「略」=开放圈画: 学生自由圈画不懂的部分,
  //                                     不本地判分, 服务端恒判正确; 有维度行才随开放LLM出维度评价)
  //   [data-annotate-content]           可划选正文容器(缺省取 q 内第一个 div 块)
  //   <button data-submit="annotate">   提交按钮(bindSubmit 统一处理)
  // 判分口径(用户拍板)：全部目标命中 且 无误圈(选到目标之外的文字) 才 correct=true；部分正确=错误。空白不参与判定。
  // 存储：params.selected=选中字符span下标数组——正文文本固定，下标跨屏/回显稳定，体积极小。
  var ANNO_STYLE = '.anno-wrap{-webkit-user-select:none;user-select:none;}' +
    '.anno-t{border-radius:2px;cursor:pointer;touch-action:none;}' +
    '.anno-t.anno-hit{background:rgba(37,99,235,.25);}' +
    '.anno-t.anno-target{box-shadow:inset 0 -0.5em 0 rgba(234,179,8,.5);}' +
    '.anno-toolbar{display:flex;gap:8px;align-items:center;margin:6px 0 2px;font-size:12px;color:#64748b;}' +
    '.anno-btn{border:1px solid #cbd5e1;background:#f8fafc;border-radius:6px;padding:2px 8px;cursor:pointer;font-size:12px;}';
  function annoEnsureStyle() {
    if (!document.getElementById('anno-runtime-style')) {
      var st = document.createElement('style');
      st.id = 'anno-runtime-style';
      st.textContent = ANNO_STYLE;
      document.head.appendChild(st);
    }
  }
  // 把容器内所有文本节点逐字包 span。剔除：空白字符、零宽字符(AI页常混入,不可见却污染判分)、
  // 屏幕外/零尺寸字符(隐藏重复文本——目标搜索与误圈判定只认学生可见的正文)，返回可见字符span数组
  function annoWrapChars(container) {
    var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) {
      var n = walker.currentNode;
      var p = n.parentNode && n.parentNode.nodeName;
      if (p === 'SCRIPT' || p === 'STYLE' || p === 'SVG') continue;
      nodes.push(n);
    }
    var spans = [];
    nodes.forEach(function(n) {
      var text = n.nodeValue;
      if (!text || !text.replace(/\\s+|[\\u200b-\\u200f\\u2060\\ufeff]/g, '')) return;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < text.length; i++) {
        var ch = text[i];
        if (/\\s/.test(ch) || /[\\u200b-\\u200f\\u2060\\ufeff]/.test(ch)) { frag.appendChild(document.createTextNode(ch)); continue; }
        var sp = document.createElement('span');
        sp.className = 'anno-t';
        sp.textContent = ch;
        frag.appendChild(sp);
        spans.push(sp);
      }
      n.parentNode.replaceChild(frag, n);
    });
    // 可见过滤: 零尺寸或落在容器外(隐藏副本/屏幕外文本)的span不参与圈画与判分, 再统一编号
    // (编号体系=过滤后顺序, 作答/回显/判分三方一致的前提; 隐藏容器的延迟初始化见 initAnnotate)
    var box = container.getBoundingClientRect();
    var visible = spans.filter(function(sp) {
      var r = sp.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.right > box.left - 1 && r.left < box.right + 1 && r.bottom > box.top - 1 && r.top < box.bottom + 1;
    });
    visible.forEach(function(sp, i) { sp.setAttribute('data-anno-i', i); });
    return visible;
  }
  function annoTargets(q, spans) {
    // 双源读取（2026-10-27 断链修复）：独立题答案在 data-annotate-answers；练习页子空契约
    // 答案在空容器 data-correct-answer——原只读前者，子空恒进开放模式被判 0 分
    var answers = (q.getAttribute('data-annotate-answers') || q.getAttribute('data-correct-answer') || '').trim();
    // 「略」=开放圈画（2026-11-05 用户拍板）：学生自由圈画（常用于圈出自己不懂的部分），
    // 不计对错、服务端恒判正确——与缺省同归开放模式；否则"略"三字会被当圈画目标，
    // 学生怎么圈都显示漏圈
    var openMode = !answers || answers === '略';
    var text = spans.map(function(sp) { return sp.textContent; }).join('');
    var targets = [];
    // 贪心不重叠占位（2026-10-28 用户拍板"支持多个字串"）：目标是题干字串即可——单个词语
    // 或词句均可、数量不限（圈动词类多目标）。题干同一词出现多次时逐目标找第一个未被
    // 占用的出现位置（重复目标如 跑|跑 绑到两处不同"跑"），与服务端 correct_annotate 的
    // 区间消耗判定同口径——原 indexOf 首现定位会把重复目标绑到同一处、圈第二处被判误圈
    var claimed = [];
    var overlaps = function(a, b) {
      return claimed.some(function(r) { return a < r[1] && b > r[0]; });
    };
    (openMode ? [] : answers.split('|')).forEach(function(t) {
      // 目标空白归一：spans 拼接文本无空白字符，目标含换行/空格（复制自原文）时须同样归一再匹配
      t = t.replace(/\\s+/g, '').trim();
      if (!t) return;
      var at = -1, cursor = 0;
      while (cursor <= text.length - t.length) {
        var hit = text.indexOf(t, cursor);
        if (hit < 0) break;
        if (!overlaps(hit, hit + t.length)) { at = hit; break; }
        cursor = hit + 1;
      }
      if (at < 0) { targets.push({ text: t, ids: [], found: false }); return; }
      claimed.push([at, at + t.length]);
      var ids = [];
      for (var i = at; i < at + t.length; i++) ids.push(String(i));
      targets.push({ text: t, ids: ids, found: true });
    });
    return { mode: openMode ? 'open' : 'scored', targets: targets };
  }
  // 选中态应用到视觉: 选中高亮 + 目标整词命中时黄底下划提示
  function annoApplySel(st) {
    st.spans.forEach(function(sp) {
      sp.classList.toggle('anno-hit', !!st.selected[sp.getAttribute('data-anno-i')]);
    });
    if (st.ansMode) {
      st.targets.forEach(function(t) {
        var allHit = t.ids.length && t.ids.every(function(id) { return st.selected[id]; });
        t.ids.forEach(function(id) { st.spans[Number(id)].classList.toggle('anno-target', !!allHit); });
      });
    }
  }
  function annoToolbar(st, q) {
    var bar = document.createElement('div');
    bar.className = 'anno-toolbar';
    bar.innerHTML = '<span class="anno-btn" data-anno-clear>清空重选</span>' +
      '<span class="anno-hint">按住拖动, 在原文上划出答案</span>';
    bar.addEventListener('click', function(e) {
      if (e.target.closest('[data-anno-clear]')) {
        st.selected = {};
        annoApplySel(st);
      }
    });
    return bar;
  }
  function initAnnotate(q) {
    if (q.__anno) return;
    annoEnsureStyle();
    var content = q.querySelector('[data-annotate-content]') || q.querySelector('div');
    if (!content) return;
    // 容器隐藏时延迟初始化(2026-10-29 修复: 教师端分类tab卡片先隐藏加载后显示——display:none 下
    // 坐标全零, 可见过滤会把全部字span误杀且编号体系错乱(实测: 右栏回显丢失/选中错位)。等可见后
    // 按原过滤逻辑初始化(编号与作答环境一致), 并重放先期到达而被挂起的恢复数据
    var box0 = content.getBoundingClientRect();
    if (box0.width === 0 && box0.height === 0) {
      q.__annoPending = true;
      var pollTimer = setInterval(function() {
        var b = content.getBoundingClientRect();
        if (!b.width && !b.height) return;
        clearInterval(pollTimer);
        if (q.__annoPending) { delete q.__annoPending; }
        initAnnotate(q);
        var pend = q.__annoRestorePending;
        if (pend) { delete q.__annoRestorePending; restoreAnnotate(q, pend); }
      }, 300);
      setTimeout(function() { clearInterval(pollTimer); }, 60000);   // 兜底停轮询防泄漏
      return;
    }
    var wrap = document.createElement('div');
    wrap.className = 'anno-wrap';
    content.parentNode.insertBefore(wrap, content);
    wrap.appendChild(content);
    var spans = annoWrapChars(content);
    var tg = annoTargets(q, spans);
    var st = { spans: spans, selected: {}, drawing: false, add: true, ansMode: tg.mode === 'scored', targets: tg.targets };
    q.__anno = st;
    wrap.parentNode.insertBefore(annoToolbar(st, q), wrap);
    function charFromEvent(e) {
      var src = e.touches && e.touches.length ? e.touches[0] : e;
      var el = document.elementFromPoint(src.clientX, src.clientY);
      return (el && el.classList && el.classList.contains('anno-t') && wrap.contains(el)) ? el : null;
    }
    function down(e) {
      var el = charFromEvent(e);
      if (!el) return;
      e.preventDefault();
      var i = el.getAttribute('data-anno-i');
      // 起点字符决定本次划选方向: 起点未选中→一路加选; 起点已选中→一路擦除
      st.add = !st.selected[i];
      if (st.add) st.selected[i] = 1; else delete st.selected[i];
      st.drawing = true;
      el.classList.toggle('anno-hit', st.add);
    }
    function move(e) {
      if (!st.drawing) return;
      var el = charFromEvent(e);
      if (!el) return;
      var i = el.getAttribute('data-anno-i');
      if (st.add ? st.selected[i] : !st.selected[i]) return;
      if (st.add) st.selected[i] = 1; else delete st.selected[i];
      el.classList.toggle('anno-hit', st.add);
    }
    function up() {
      if (!st.drawing) return;
      st.drawing = false;
      annoApplySel(st);
    }
    q.addEventListener('mousedown', down);
    q.addEventListener('mousemove', move);
    q.addEventListener('mouseup', up);
    q.addEventListener('touchstart', down, { passive: false });
    q.addEventListener('touchmove', function(e) { if (st.drawing) { e.preventDefault(); move(e); } }, { passive: false });
    q.addEventListener('touchend', up);
  }
  function collectAnnotate(q) {
    var st = q.__anno;
    if (!st) return null;
    var sel = Object.keys(st.selected);
    // 连续选中段聚合成词句文本
    function groupText(ids) {
      var sorted = ids.map(Number).sort(function(a, b) { return a - b; });
      var out = [], buf = [];
      sorted.forEach(function(i) {
        if (buf.length && i === buf[buf.length - 1] + 1) { buf.push(i); return; }
        if (buf.length) out.push(buf);
        buf = [i];
      });
      if (buf.length) out.push(buf);
      return out.map(function(g) { return g.map(function(i) { return st.spans[i].textContent; }).join(''); });
    }
    if (!st.ansMode) {
      // 开放型: 无标准答案, 不本地判分, 划中文本交后端AI批改
      var openText = groupText(sel);
      return { answer: openText.join('；'), correct: false, score: 0,
               params: { selected: sel, circled: openText, mode: 'open' }, reason: '开放圈画·待批改' };
    }
    var hit = [], miss = [];
    var targetIds = {};
    st.targets.forEach(function(t) {
      t.ids.forEach(function(id) { targetIds[id] = 1; });
      var allHit = t.found && t.ids.length && t.ids.every(function(id) { return st.selected[id]; });
      (allHit ? hit : miss).push(t.text);
    });
    // 误圈: 选中但不在任何目标里的连续文字(空白字符未包span天然不计)
    var extra = groupText(sel.filter(function(i) { return !targetIds[i]; }));
    var ok = sel.length > 0 && miss.length === 0 && extra.length === 0;
    // answer=机器格式（2026-10-27 批改链契约）：选中段按文档序'；'拼接——服务端 correct_annotate
    // 按段解析判分（全对制同口径）；圈对/漏圈/误圈明细在 reason+params（回显走 params.selected 不依赖 answer）
    var segTexts = groupText(sel);
    var reason = ok ? '圈画全部命中，无误圈' :
      ('有目标未圈到' + (miss.length ? '（漏圈：' + miss.join('；') + '）' : '') + (extra.length ? '，存在误圈' : ''));
    return { answer: segTexts.join('；'), correct: ok, score: ok ? 1 : 0,
             params: { selected: sel, segs: segTexts, hit: hit, miss: miss, extra: extra, mode: 'scored' },
             reason: reason };
  }
  function restoreAnnotate(q, d) {
    var st = q.__anno;
    if (q.__annoPending) { q.__annoRestorePending = d; return; }   // 引擎隐藏待初始化: 挂起恢复, 显示后重放
    if (!st || !d.params) return;
    st.selected = {};
    (d.params.selected || []).forEach(function(i) { st.selected[i] = 1; });
    annoApplySel(st);
    if (d.submitted) { var b = findSubmitBtn(q, 'annotate'); if (b) b.click(); }
  }

  // ====== 6d. 组合空预填充（2026-10-21 组合全文）：按关联段序向宿主拉被关联题作答，空态回填 ======
  // 契约：组合空各段 textarea 带 data-ref-index（=关联选择顺序，aicourse 后处理按 plan 关联声明重建注入）。
  // 数据流：runtime 发 request_related_answers → 宿主按本题 error_info.refs_resolved 逐项查批改记录
  // → 回传 related_answers.texts（按段序数组）→ 逐段回填。回填仅限空态——学生已修改/已回显的段落
  // 绝不覆盖；「重新拉取关联作答」按钮显式确认后才覆盖重拉。
  var comboPrefillPending = {};   // key=questionId:subId → true(空态回填)/'overwrite'(确认覆盖)

  function comboPrefillTip(msg, isError) {
    var t = document.createElement('div');
    t.textContent = msg;
    t.setAttribute('style', 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:99999;'
      + 'max-width:86%;padding:10px 16px;border-radius:8px;font-size:13px;line-height:1.6;'
      + (isError
        ? 'background:#2b1f1f;color:#ffd7d7;border:1px solid #b46b6b;'
        : 'background:#1f2b24;color:#d7ffe3;border:1px solid #6bb486;')
      + 'box-shadow:0 4px 16px rgba(0,0,0,.35);');
    document.body.appendChild(t);
    setTimeout(function() { if (t.parentNode) t.parentNode.removeChild(t); }, 6000);
  }

  function comboPrefillApply(sub, texts, overwrite) {
    if (!Array.isArray(texts)) return;
    var filled = 0, emptyIdx = [];
    sub.querySelectorAll('textarea[data-ref-index]').forEach(function(ta) {
      var idx = parseInt(ta.getAttribute('data-ref-index'), 10) || 0;
      var text = texts[idx - 1] === undefined || texts[idx - 1] === null ? '' : String(texts[idx - 1]);
      if (!text) { emptyIdx.push(idx); return; }
      if (!overwrite && String(ta.value || '').trim() !== '') return; // 空态保护：已有内容不覆盖
      ta.value = text;
      ta.dispatchEvent(new Event('input', { bubbles: true }));        // 触发行为统计/高度自适应
      filled++;
    });
    if (filled) comboPrefillTip(emptyIdx.length
      ? '已按关联作答预填 ' + filled + ' 段；第 ' + emptyIdx.join('、') + ' 段的关联作答尚未生成（未提交或未批改完成），可稍后点「重新拉取」'
      : '已按关联作答预填 ' + filled + ' 段，可直接修改润色后提交');
  }

  function comboPrefillRequest(q, sub, overwrite) {
    var key = q.getAttribute('data-question-id') + ':' + (sub.getAttribute('data-sub-id') || '');
    comboPrefillPending[key] = overwrite ? 'overwrite' : true;
    var msg = {
      type: 'request_related_answers',
      questionId: q.getAttribute('data-question-id'),
      subId: sub.getAttribute('data-sub-id') || '',
      segCount: sub.querySelectorAll('textarea[data-ref-index]').length
    };
    try { window.parent.postMessage(msg, window.parent.location.origin); }
    catch (e) { delete comboPrefillPending[key]; }
  }

  function bindComboPrefill(q, sub) {
    if (!sub.querySelectorAll('textarea[data-ref-index]').length) return; // 旧课件无段序契约，跳过
    if (sub.__comboPrefillBound) return;
    sub.__comboPrefillBound = true;
    comboPrefillRequest(q, sub, false); // 进入即拉一次（空态回填）
    // 「重新拉取关联作答」：等批改完成/补答后手动刷新，显式确认覆盖
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '↻ 重新拉取关联作答';
    btn.setAttribute('style', 'display:inline-flex;align-items:center;gap:4px;margin:8px 0 4px;padding:5px 16px;'
      + 'font-size:12px;font-weight:600;color:#3d6fb4;background:#f4faff;border:1px dashed #9cc0e8;'
      + 'border-radius:999px;cursor:pointer;transition:background .15s,border-color .15s;');
    btn.addEventListener('mouseenter', function() { btn.style.background = '#e8f3fd'; btn.style.borderColor = '#5a8fd6'; });
    btn.addEventListener('mouseleave', function() { btn.style.background = '#f4faff'; btn.style.borderColor = '#9cc0e8'; });
    btn.addEventListener('click', function() {
      if (confirm('将按关联题的最新作答重新填充各段（当前修改会被替换），继续？')) {
        comboPrefillRequest(q, sub, true);
      }
    });
    // 按钮锚=最近的提交按钮：空内优先（单题形态），空外页级次之（v2空容器形态按钮挂题容器），
    // 都没有则挂空容器尾——保证重拉入口恒在
    var submitBtn = sub.querySelector('[data-submit="combo_composition"]') || sub.querySelector('[data-submit]')
      || (q !== sub ? q.querySelector('[data-submit]') : null);
    if (submitBtn && submitBtn.parentNode) submitBtn.parentNode.insertBefore(btn, submitBtn);
    else sub.appendChild(btn);
  }

  // 宿主回传：按在途请求的覆盖标记应用（不在途的回传一律丢弃，防串题/串空）
  window.addEventListener('message', function(event) {
    var d = event.data;
    if (!d || d.type !== 'related_answers') return;
    var key = d.questionId + ':' + (d.subId || '');
    var pend = comboPrefillPending[key];
    if (!pend) return;
    delete comboPrefillPending[key];
    var q = document.querySelector('[data-question-id="' + d.questionId + '"]');
    if (!q) return;
    var sub = d.subId ? q.querySelector('[data-sub-id="' + d.subId + '"]') : q;
    if (!sub) return;
    if (d.error) { comboPrefillTip(d.error, true); return; }
    comboPrefillApply(sub, d.texts, pend === 'overwrite');
  });

  // v2(2026-10-18)：exercise=练习页统一空容器结构（页面=子题数组·无单空/多空二分），
  // 收集/回显复用空容器通路（逐空按 data-sub-type 分发）；multi 键已彻底退役不留兼容
  var COLLECTORS = { drag: collectDrag, sort: collectSort, match: collectMatch, memory: collectMemory, choice: collectChoice, judge: collectJudge, fill: collectFill, combo_composition: collectFill, voice_read: collectVoiceRead, voice_speak: collectVoiceSpeak, voice: collectVoiceSpeak, reflection: collectReflection, exercise: collectMulti, annotate: collectAnnotate };

  function bindSubmit(q) {
    var qtype = getQType(q);
    var btn = findSubmitBtn(q, qtype); if (!btn) return;
    btn.addEventListener('click', function() {
      var fn = COLLECTORS[qtype]; if (!fn) return;
      var data = fn(q); if (!data) return;
      var behavior = getBehaviorData(q);
      data.pause_count = behavior.pause_count;
      data.revision_count = behavior.revision_count;
      if (window.AnswerCollector && window.AnswerCollector.collect) window.AnswerCollector.collect(data, q); // q=子题容器：一题多空时按子题聚合
      // 选择/判断结果固定：提交后按判分着色选中项并锁定（multi 逐空处理）
      if (qtype === 'choice' || qtype === 'judge') {
        markChoiceResult(q, data.correct === true);
        lockChoiceScope(q);
      } else if (qtype === 'exercise') {
        markMultiChoiceSubs(q, data.params && data.params.subAnswers);
      }
      btn.style.display = 'none';
      showFeedback(q); // 提交后展示参考反馈区
    });
  }

  // 清除按钮（后端HTML带 data-clear="题型" 标记，如连线题的"清除连线"）
  function bindClear(q) {
    q.querySelectorAll('[data-clear]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var clearType = btn.getAttribute('data-clear') || getQType(q);
        if (clearType === 'match') {
          q.querySelectorAll('[data-match-source]').forEach(function(src) {
            src.removeAttribute('data-connected-target');
            src.classList.remove('match-selected');
          });
          if (q._matchState && q._matchState.selectedSource) q._matchState.selectedSource = null;
          drawMatchLines(q);
        }
      });
    });
  }

  // ====== 8. 回显恢复 ======
  function restoreDrag(q, d) {
    q.querySelectorAll('[data-drag-id]').forEach(function(el) { var t = el.closest('[data-target-id]'); if (t) { el.style.position = 'relative'; el.style.left = '0'; el.style.top = '0'; } });
    if (d.submitted) { var b = findSubmitBtn(q, 'drag'); if (b) b.click(); }
  }
  function restoreSort(q, d) {
    updateSortNums(q);
    q.querySelectorAll('[data-slot]').forEach(function(s) { if (s.querySelector('[data-sort-id]')) { s.classList.add('filled'); var hint = s.querySelector('.slot-empty-hint'); if (hint) hint.style.display = 'none'; } });
    if (d.submitted) { var b = findSubmitBtn(q, 'sort'); if (b) b.click(); }
  }
  function restoreMatch(q, d) { drawMatchLines(q); if (d.submitted) { var b = findSubmitBtn(q, 'match'); if (b) b.click(); } }
  // 翻牌回显：按记录 answer（规范对串 1-5,2-6…）恢复每对两张卡 matched+flipped+data-matched-pair；
  // 与 answer-collector 的 matchedPairs 分支同口径幂等。原实现只给 DOM 里已带 .matched 的卡加 .flipped、
  // 不读记录，新开页面恒为空操作
  function restoreMemory(q, d) {
    String(d.answer || '').split(',').forEach(function(p) {
      var parts = p.trim().split('-'); if (parts.length !== 2) return;
      var key = memoryPairKey(parts[0], parts[1]);
      parts.forEach(function(id) {
        var c = q.querySelector('[data-card-id="' + id.trim() + '"]');
        if (c) { c.classList.add('matched', 'flipped'); c.setAttribute('data-matched-pair', key); }
      });
    });
    if (d.submitted) { var b = findSubmitBtn(q, 'memory'); if (b) b.click(); }
  }
  function restoreChoice(q, d) { if (d.answer) { var o = q.querySelector('[data-option="' + d.answer + '"]'); if (o) { o.classList.add('selected'); o.setAttribute('data-selected', 'true'); if (o.tagName === 'INPUT' && (o.type === 'radio' || o.type === 'checkbox')) o.checked = true; } } if (d.params && d.params.studentReason) { var ta = q.querySelector('textarea'); if (ta) ta.value = d.params.studentReason; } if (d.submitted) { markChoiceResult(q, d.params && d.params.isCorrect); lockChoiceScope(q); var b = findSubmitBtn(q, 'choice'); if (b) b.click(); } }
  function restoreJudge(q, d) { if (d.answer) { var b2 = q.querySelector('.judge-btn[data-option="' + d.answer + '"]') || q.querySelector('[data-option="' + d.answer + '"]'); if (b2) { b2.classList.add('selected'); if (b2.tagName === 'INPUT' && (b2.type === 'radio' || b2.type === 'checkbox')) b2.checked = true; } } if (d.submitted) { markChoiceResult(q, d.params && d.params.isCorrect); lockChoiceScope(q); var b = findSubmitBtn(q, 'judge'); if (b) b.click(); } }
  function restoreFill(q, d) {
    // 回显填空值（公式空=普通输入框，按 id 优先、data-fill-id 回退；手写公式走下方笔迹重绘）
    if (d.answer) {
      var pairs = d.answer.split('|');
      pairs.forEach(function(p) {
        var parts = p.split(':');
        var fid = parts[0]; var val = parts[1] || '';
        var el = document.getElementById(fid) || q.querySelector('input[data-fill-id="' + fid + '"], textarea[data-fill-id="' + fid + '"]');
        if (el) el.value = val;
      });
    }
    // 回显手写输入：从提交数据（handwrite_dots，后端笔批改统一 x/y 分离格式）恢复笔迹并重绘预览。
    // 笔迹总是显示(OSS点数据是作答证据); 识别文本态(value非空=批改侧已回写识别结果)时键盘可编辑,
    // 笔迹仅作展示留存——文本与笔迹两域独立: 键盘改文本不动笔迹, 手写板改笔迹不动文本
    if (d.params && d.params.handwrite_dots) {
      var hwMap = d.params.handwrite_dots;
      for (var hwId in hwMap) {
        if (!hwMap.hasOwnProperty(hwId)) continue;
        var hwEl = document.getElementById(hwId);
        var hw = hwMap[hwId];
        if (hwEl && hw && hw.dots && hw.dots.length) {
          // dots → 点对转换（手写板内部统一消费点对格式）
          hwEl.__formulaHandwriting = {
            w: hw.w, h: hw.h,
            strokes: hw.dots.map(function(dt) {
              return dt.x.map(function(xv, i) { return [xv, dt.y[i]]; });
            })
          };
          if (!hwEl.value) {
            // 无识别文本: 旧手写态语义(锁定键盘、答案载体是笔迹)
            hwEl.readOnly = true;
            hwEl.value = '';
            hwEl.placeholder = '已手写（点缩略图可重写）';
          }
          renderFormulaHwPreview(hwEl);
        }
      }
    }
    if (d.submitted) { var b = findSubmitBtn(q, 'fill'); if (b) b.click(); }
  }
  // 语音回放控件(2026-10-31 用户需求): 回显时学生能回听自己提交的 OSS 录音——原生 audio
  // 控件最稳; 已有控件不重复插(重复回显幂等); URL 非法(空/非http)不插
  function insertVoicePlayback(q, url) {
    if (!url || !/^https?:\\/\\//.test(url)) return;
    if (q.querySelector('.voice-playback')) return;
    var box = document.createElement('div');
    box.className = 'voice-playback';
    box.style.cssText = 'margin-top:8px;';
    var audio = document.createElement('audio');
    audio.controls = true;
    audio.preload = 'none';
    audio.src = url;
    audio.style.cssText = 'width:100%;max-width:420px;';
    box.appendChild(audio);
    var area = q.querySelector('.voice-area') || q.querySelector('.voice-input-area');
    (area || q).appendChild(box);
  }
  function restoreVoiceSpeak(q, d) {
    // 口语回显(2026-10-31 必须录音口径): answer 恒为录音 OSS 地址——只回放不写识别区;
    // 存量打字作答(文字形态)仍写识别区兼容显示
    if (d.answer && !/^https?:\\/\\//.test(d.answer)) {
      var recognizedArea = q.querySelector('[data-voice-recognized]');
      if (recognizedArea) recognizedArea.textContent = '识别结果：' + d.answer;
    }
    insertVoicePlayback(q, (d.params && d.params.voiceAudioUrl) || d.answer);
    if (d.submitted) { var b = findSubmitBtn(q, 'voice_speak'); if (b) b.click(); }
  }
  function restoreVoiceRead(q, d) {
    if (d.answer) {
      q.setAttribute('data-voice-audio-url', d.answer);
      var st = q.querySelector('.voice-status');
      if (st) st.textContent = '已提交，等待批改';
    }
    // 回放: 跟读 answer 恒为录音 OSS 地址
    insertVoicePlayback(q, d.answer);
    if (d.submitted) { var b = findSubmitBtn(q, 'voice_read'); if (b) b.click(); }
  }
  function restoreReflection(q, d) {
    var txt = (d.params && d.params.reflection_answer) || d.answer || '';
    var tas = q.querySelectorAll('textarea');
    if (tas.length === 1 && txt) tas[0].value = txt;
    else if (tas.length > 1 && txt) { var parts = txt.split('\\n'); for (var i = 0; i < tas.length && i < parts.length; i++) tas[i].value = parts[i]; }
    if (d.params && d.params.ratings) restoreRatings(q, d.params.ratings);
    if (d.submitted) {
      tas.forEach(function(ta) { ta.setAttribute('readonly', 'true'); });
      var btn = findSubmitBtn(q, 'reflection'); if (btn) btn.style.display = 'none';
    }
  }
  // 复合题回显：逐空调用该空题型的 RESTORER 恢复交互态（表单值/拖拽位置已由 collector
  // 的 applyRestoreUniversal 按 scope=空容器 恢复，这里补 selected/画线/序号等视觉态）。
  // 空级恢复一律 submitted:false——复合题只有题级一个提交按钮，不能让空级 restorer 去
  // click 提交（会触发重复收集）；已提交态由本题自己处理（隐藏题级按钮+展示反馈区）。
  function restoreMulti(q, d) {
    var sa = (d.params && d.params.subAnswers) || {};
    q.querySelectorAll('[data-sub-id]').forEach(function(sub) {
      var sid = sub.getAttribute('data-sub-id');
      var info = sa[sid] || {};
      var subType = info.type || sub.getAttribute('data-sub-type') || '';
      // 单选/判断空恢复前清掉空内既有 selected（同页重复回显时避免双选中）
      if (subType === 'choice' || subType === 'judge') {
        sub.querySelectorAll('.selected').forEach(function(el) { el.classList.remove('selected'); });
      }
      var fn = RESTORERS[subType];
      if (!fn) return;
      try { fn(sub, { answer: info.answer || '', params: info.params || {}, submitted: false }); }
      catch (err) { console.error('[question-runtime] restoreMulti ' + sid + '(' + subType + ') failed:', err); }
    });
    if (d.submitted) {
      // 已提交的选择/判断空：按提交时判分着色选中项并锁定
      markMultiChoiceSubs(q, sa);
      var btn = findSubmitBtn(q, 'exercise'); if (btn) btn.style.display = 'none';
      showFeedback(q);
    }
  }
  var RESTORERS = { drag: restoreDrag, sort: restoreSort, match: restoreMatch, memory: restoreMemory, choice: restoreChoice, judge: restoreJudge, fill: restoreFill, combo_composition: restoreFill, voice_read: restoreVoiceRead, voice_speak: restoreVoiceSpeak, voice: restoreVoiceSpeak, reflection: restoreReflection, exercise: restoreMulti, annotate: restoreAnnotate };

  // ====== 8z. 通用Tab切换(2026-11-03 工具箱样例卡tab点不动修复) ======
  // LLM 生成的工具箱/讲解卡常用 tab-btn[data-target] 切换样例面板，但自带的 <script> 被
  // _strip_all_scripts / stripAllScripts 剥掉(纯HTML契约：JS 全由 runtime 注入)——tab 点不动。
  // 此处注入通用处理：点击 .tab-btn[data-target] → 同容器内 tab 互斥 active + 目标面板 active。
  // 兼容多种面板类名(.sample-card / .tab-panel / .tab-content / [data-tab-panel])
  function initToolTabs() {
    document.querySelectorAll('.tab-btn[data-target]').forEach(function(btn) {
      if (btn.__tabBound) return;
      btn.__tabBound = true;
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        var scope = btn.closest('.tabs, [role="tablist"], [class*="tab-bar"], [class*="tabBar"]') || btn.parentElement;
        if (!scope) return;
        // 同 scope 内所有 tab 互斥
        scope.querySelectorAll('.tab-btn').forEach(function(t) { t.classList.remove('active'); });
        btn.classList.add('active');
        // 找到 tab 容器的父级(面板的公共父)，在面板区域内切换 active
        var panelScope = scope.parentElement || document;
        // 目标面板：按 id 精确匹配
        var targetSel = btn.getAttribute('data-target');
        var target = document.getElementById(targetSel) || panelScope.querySelector('#' + targetSel);
        if (!target) return;
        // 同层面板互斥：与目标同级的 .sample-card / .tab-panel 等都去掉 active
        var panelCls = '.sample-card, .tab-panel, .tab-content, [data-tab-panel]';
        var siblings = target.parentElement.querySelectorAll(panelCls);
        siblings.forEach(function(p) { p.classList.remove('active'); });
        target.classList.add('active');
      });
    });
  }

  // ====== 9. 语音讲解（浏览器自带 speechSynthesis 朗读）======
  // 朗读文本语言判定(整体判定不分段): 拉丁字母显著多于汉字→英语
  function detectNarrationLang(text) {
      var cjk = (text.match(/[\\u4e00-\\u9fff]/g) || []).length;
    var latin = (text.match(/[A-Za-z]/g) || []).length;
    return (latin > cjk * 2 && latin >= 10) ? 'en' : 'zh';
  }

  // 缺语音包的平台化安装指引(明确报错, 不用其他语言语音兜底——宁可不读不能读错)
  function narrationVoiceError(lang) {
    var langName = lang === 'en' ? '英语' : '中文';
    var ua = navigator.userAgent;
    var guide;
    if (/Android/i.test(ua)) {
      guide = '请到 系统设置 → 系统 → 语言和输入法 → 文字转语音输出，选择 Google 或 讯飞 引擎并安装' + langName + '语音数据后重试';
    } else if (/iPhone|iPad|iPod/i.test(ua)) {
      guide = '请到 设置 → 辅助功能 → 朗读内容 → 声音，下载' + langName + '语音后重试';
    } else if (/Windows/i.test(ua)) {
      guide = '请到 设置 → 时间和语言 → 语音，添加' + langName + '语音包后重试；或改用 Edge 浏览器（自带在线' + langName + '语音，无需安装）';
    } else {
      guide = '请在系统设置中安装' + langName + '语音包后重试，或改用 Edge/Chrome 浏览器';
    }
    return '⚠️ 当前设备缺少' + langName + '语音包，无法朗读。' + guide;
  }

  // 朗读错误提示(注入题目页面内的悬浮提示条, 8秒自动消失)
  function showNarrationError(msg) {
    var t = document.createElement('div');
    t.textContent = msg;
    t.setAttribute('style', 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:99999;'
      + 'max-width:86%;padding:12px 18px;border-radius:8px;font-size:14px;line-height:1.6;'
      + 'background:#2b1f1f;color:#ffd7d7;border:1px solid #b46b6b;box-shadow:0 4px 16px rgba(0,0,0,.35);');
    document.body.appendChild(t);
    setTimeout(function() { if (t.parentNode) t.parentNode.removeChild(t); }, 8000);
  }

  // iframe 卸载/替换自终止(2026-10-21 语音治理): speechSynthesis 播报队列不随 iframe 销毁停止——
  // 宿主切换题/工具/关预览卸载本帧时, 语音在此自我终止(宿主层 cancelAllSpeech 之外的兜底全覆盖)
  window.addEventListener('pagehide', function () {
    try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (e) { /* 卸载路径不抛错 */ }
  });

  function initNarration() {
    // card 卡片支招工具页不需要语音（2026-10-13 需求）：隐藏朗读按钮、跳过事件绑定。
    // 台词与按钮结构保留在 HTML 产物中，未来恢复语音只需移除本段。
    if (document.querySelector('[data-tool-role="card"]')) {
      var muteStyle = document.createElement('style');
      muteStyle.textContent = '.narration-btn{display:none !important;}';
      document.head.appendChild(muteStyle);
      return;
    }
    // 按钮文案统一（2026-10-28 用户需求）：空闲=🔊 语音 / 朗读中=⏹ 停止语音——不再透出
    // HTML 自带文案（存量产物「听讲解」在朗读题目场景语义不准），运行时强制覆盖
    var IDLE_TXT = '🔊 语音', BUSY_TXT = '⏹ 停止语音';
    // 页面级单播标记：新播放前停旧按钮音频（系统TTS由全局 cancel 保证），同一时间只读一段
    var activeBtn = null;

    function resetBtn(btn) {
      btn.__narrating = false;
      btn.textContent = IDLE_TXT;
      btn.title = '播放语音';
    }

    function stopNarration(btn) {
      if (btn && btn.__narrAudio) btn.__narrAudio.pause();
      if (btn) resetBtn(btn);
      if (activeBtn === btn) activeBtn = null;
    }

    // 产物预合成音频播放（2026-10-28 质检生成语音方案）：容器带 data-narration-audio 时优先
    // 走 <audio>——mp3 全年代平板兼容、即点即播；加载失败(URL失效/网络)清引用回落系统TTS
    function playAudio(btn) {
      var audio = btn.__narrAudio;
      audio.onended = function() { stopNarration(btn); };
      audio.onerror = function() {
        var wasPlaying = btn.__narrating;
        btn.__narrAudio = null;   // 清引用：下次点击自然走系统TTS路径
        stopNarration(btn);
        if (wasPlaying && btn.__narrText) speakViaSynthesis(btn, btn.__narrText);
      };
      audio.currentTime = 0;   // 重复播放从头开始
      btn.__narrating = true;
      btn.textContent = BUSY_TXT;
      btn.title = '停止语音';
      activeBtn = btn;
      var p = audio.play();
      // 老浏览器 play() 无返回值（无 Promise），启动失败靠 onerror 事件回落
      if (p && p.catch) p.catch(function() { speakViaSynthesis(btn, btn.__narrText); });
    }

    // 系统TTS路径（存量产物/未生成音频/音频加载失败兜底）：语言检测+语音包校验+800ms
    // speak层兜底，逻辑与 2026-10-28 语音数组化版本一致，整体迁移为独立函数供两处调用
    function speakViaSynthesis(btn, text) {
      // 语言检测 + 语音包校验: 按文本语言选语音, 缺对应语音包则明确报错并给安装指引, 不兜底
      var lang = detectNarrationLang(text);
      var voices = window.speechSynthesis.getVoices();
      if (voices.length) {
        var matched = voices.filter(function(v) {
          return (lang === 'en') ? /^en([-_]|$)/i.test(v.lang) : /^zh([-_]|$)/i.test(v.lang);
        });
        if (!matched.length) { showNarrationError(narrationVoiceError(lang)); return; }
      }
      var utter = new SpeechSynthesisUtterance(text);
      utter.lang = lang === 'en' ? 'en-US' : 'zh-CN';
      if (voices.length) utter.voice = voices.filter(function(v) {
        return (lang === 'en') ? /^en([-_]|$)/i.test(v.lang) : /^zh([-_]|$)/i.test(v.lang);
      })[0];
      utter.rate = 1;
      utter.onstart = function() { btn.__narrating = true; btn.textContent = BUSY_TXT; btn.title = '停止语音'; activeBtn = btn; };
      utter.onend = function() { resetBtn(btn); if (activeBtn === btn) activeBtn = null; };
      utter.onerror = function() { resetBtn(btn); if (activeBtn === btn) activeBtn = null; };
      window.speechSynthesis.speak(utter);
      // speak层失败兜底: 800ms 后仍未开始(语音包缺失/不可用), 明确报错而非静默复原
      setTimeout(function() {
        if (!btn.__narrating && !window.speechSynthesis.speaking) {
          resetBtn(btn);
          showNarrationError(narrationVoiceError(lang));
        }
      }, 800);
    }

    document.querySelectorAll('.narration-btn').forEach(function(btn) {
      var container = btn.closest('[data-narration]'); if (!container) return;
      // 只读卡豁免(2026-10-29): 回显卡片注入 __ANSWER_READONLY__ 后只读样式一刀切禁点全部 button,
      // 语音按钮属查看功能(同✍手写按钮先例), 不豁免则教师端回显卡片/学生端浏览卡语音按钮点不动
      btn.setAttribute('data-ro-allow', '1');
      btn.textContent = IDLE_TXT;
      btn.title = '播放语音';
      var audioUrl = container.getAttribute('data-narration-audio');
      if (audioUrl) {
        // 页面加载即预热：提前拉取缓存音频（每页条数少，内存可控），点击时即点即播
        var pre = new Audio(audioUrl);
        pre.preload = 'auto';
        pre.load();
        btn.__narrAudio = pre;
      }
      btn.addEventListener('click', function() {
        var text = toSpeakableText(container.getAttribute('data-narration')); if (!text) return;
        btn.__narrText = text;
        // toggle：本按钮朗读中再点 = 停止；否则总是先清队列再播
        // （不检查全局 speaking/pending——路由切换时 cancelAllSpeech 的空 utterance 会让队列
        //   长期挂 pending，旧逻辑首次点击只 cancel 不播放，表现为"点了没声音"）
        if (btn.__narrating) {
          if (btn.__narrAudio) btn.__narrAudio.pause();
          if (window.speechSynthesis) window.speechSynthesis.cancel();
          resetBtn(btn);
          return;
        }
        if (activeBtn && activeBtn !== btn) stopNarration(activeBtn);   // 页内互斥：停其他按钮的音频
        if (window.speechSynthesis) window.speechSynthesis.cancel();
        // 优先产物预合成音频；无属性（存量/未生成）走系统TTS，两者台词文字同源 data-narration
        if (btn.__narrAudio) { playAudio(btn); return; }
        if (!window.speechSynthesis) { showNarrationError('⚠️ 当前浏览器不支持语音朗读，请改用 Chrome 或 Edge 浏览器'); return; }
        speakViaSynthesis(btn, text);
      });
    });
    // 语音包异步加载完成事件（首次 getVoices 可能为空，加载后无需处理，下次点击生效）
    if (!window.__narrationVoicesBound && window.speechSynthesis) {
      window.__narrationVoicesBound = true;
      window.speechSynthesis.onvoiceschanged = function() {};
    }
  }

  // ====== 10. 主初始化 ======
  // CSP 内联事件迁移（2026-10-29 教师端 strict-dynamic 根因补全）: 产物 HTML 里的内联
  // onclick="fn('arg')" 属性被 CSP 拦截失效——迁移为 addEventListener 调全局函数。
  // 静态元素全量扫一遍 + MutationObserver 持续迁移后续动态注入节点(工具页自身脚本常
  // innerHTML 动态生成带 onclick 的弹窗等, 实测阅读材料工具 4 个 modal-close 即此形态);
  // 存量+新增产物通吃, 题目/工具 iframe 共用本运行时全覆盖; 非简单调用形态保留属性并
  // console.warn 留痕(不静默), 待生成侧规范根治
  var HANDLER_RE = /^on(click|dblclick|change|input|keyup|keydown|keypress|mouseover|mouseout|mousedown|mouseup|focus|blur|submit|touchstart|touchend|touchmove)$/;
  function _migrateAttr(el, attr) {
    // 注意: 本文件 export default 为模板字符串, 正则反斜杠必须双写(\\d)——单写会被字符串
    // 解析折叠(d)导致正则失效(2026-10-29 实测: 参数形态校验恒不过即此根因)
    var call = String(attr.value).trim().match(/^([A-Za-z_$][\\w$]*)\\(\\s*(.*?)\\s*\\)$/);
    if (!call) {
      console.warn('[CSP-Migrate] 内联事件形态未迁移(需生成侧规范治理):', attr.name, attr.value.slice(0, 60));
      return;
    }
    var fnName = call[1], argSrc = call[2];
    var args = [];
    if (argSrc) {
      // 参数仅支持 单引号/双引号字符串与数字 的简单形态(产物内联事件实际写法)
      var m = argSrc.match(/^(?:'([^']*)'|"([^"]*)"|(-?\\d+(?:\\.\\d+)?))$/);
      if (!m) { console.warn('[CSP-Migrate] 内联事件参数形态未迁移:', attr.value.slice(0, 60)); return; }
      args = [m[1] !== undefined ? m[1] : (m[2] !== undefined ? m[2] : parseFloat(m[3]))];
    }
    el.addEventListener(attr.name.slice(2), function () {
      if (typeof window[fnName] === 'function') window[fnName].apply(null, args);
      else console.warn('[CSP-Migrate] 目标函数未定义:', fnName);
    });
    el.removeAttribute(attr.name);   // 移除被 CSP 拦截的无效属性(消除报错与误导)
  }
  function _migrateEl(el) {
    if (el.nodeType !== 1) return;
    var attrs = [];
    for (var j = 0; j < el.attributes.length; j++) {
      if (HANDLER_RE.test(el.attributes[j].name)) attrs.push(el.attributes[j]);
    }
    for (var k = 0; k < attrs.length; k++) _migrateAttr(el, attrs[k]);
    if (el.querySelectorAll) {
      var all = el.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) {
        var sub = [];
        for (var j2 = 0; j2 < all[i].attributes.length; j2++) {
          if (HANDLER_RE.test(all[i].attributes[j2].name)) sub.push(all[i].attributes[j2]);
        }
        for (var k2 = 0; k2 < sub.length; k2++) _migrateAttr(all[i], sub[k2]);
      }
    }
  }
  function migrateInlineHandlers() {
    var root = document.querySelectorAll('*');
    for (var i = 0; i < root.length; i++) _migrateEl(root[i]);
    // 持续迁移: 页面自身脚本动态注入的节点(只监听 childList, removeAttribute 不触发回环)
    var mo = new MutationObserver(function (records) {
      for (var r = 0; r < records.length; r++) {
        var nodes = records[r].addedNodes;
        for (var n = 0; n < nodes.length; n++) _migrateEl(nodes[n]);
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    migrateInlineHandlers();   // 最先执行: 后续引擎初始化与用户交互都依赖事件可用
    // 只读预览模式（2026-10-28 浏览模式串扰根因修复）：宿主注入 window.__ANSWER_READONLY__ 的
    // 预览卡片按只读初始化——隐藏提交钮、交互控件禁指针、不挂手写/提交/清除；题型引擎照常
    // 初始化（回显恢复依赖 DOM 结构）；collector 侧同标记拦截为双保险。
    // 教师演示模式（2026-10-29 宿主注入 __TEACHER_DEMO__）：交互全放开供课堂演示正确做法,
    // 仅保留提交钮隐藏(collector 的提交拦截照常, 演示不产生任何数据)
    var RO = !!window.__ANSWER_READONLY__;
    var DEMO = !!window.__TEACHER_DEMO__;
    if (RO && !document.getElementById('question-readonly-style')) {
      var roStyle = document.createElement('style');
      roStyle.id = 'question-readonly-style';
      if (DEMO) {
        roStyle.textContent = '[data-question-id] [data-submit]{display:none!important}';
      } else {
      // 只读清单（2026-10-28 全量审计补全）：逐引擎枚举交互元素——choice 的 div 选项
      // [data-option]/judge 的 .judge-btn/翻牌 [data-card-id]/星级 [data-star]/圈画工具条
      // .anno-btn/放置目标 [data-target-id][data-category] 原均漏网（div/span 形态不吃 button 选择器）
      roStyle.textContent = '[data-question-id] button:not([data-ro-allow]),[data-question-id] input,[data-question-id] textarea,[data-question-id] select,[data-question-id] .anno-t,[data-question-id] .anno-btn,[data-question-id] [draggable="true"],[data-question-id] .sort-item,[data-question-id] [data-drag-id],[data-question-id] [data-match-source],[data-question-id] [data-match-target],[data-question-id] [data-target-id],[data-question-id] [data-option],[data-question-id] [data-card-id],[data-question-id] [data-star]{pointer-events:none!important}'
        + '[data-question-id] [data-submit]{display:none!important}';
      }
      document.head.appendChild(roStyle);
    }
    // 星级选中态兜底样式（AI 自绘星星通常没写 .selected 样式，选中必须有可见反馈）
    if (!document.getElementById('rating-stars-runtime-style')) {
      var rsStyle = document.createElement('style');
      rsStyle.id = 'rating-stars-runtime-style';
      rsStyle.textContent = '.rating-stars [data-star]{cursor:pointer;} .rating-stars [data-star].selected{color:#f7b500 !important; text-shadow:0 0 4px rgba(247,181,0,.45);}';
      document.head.appendChild(rsStyle);
    }
    document.querySelectorAll('[data-question-id]').forEach(function(q) {
      var qtype = getQType(q);
      initBehaviorTracking(q);
      try {
        // 全子题化(2026-11-02 用户拍板·单题概念退役): 题级只认三种页型——exercise 练习页(作答题
        // 唯一形态, 引擎按空级 data-sub-type 分发)、lecture/reflection 无题级引擎;
        // 漂移单题形态 HTML 由 aicourse 生成校验拦截转 exercise, 运行时再遇到=契约破裂显式暴露
        if (qtype === 'exercise') {
          // 练习页=区域数组（v2 2026-10-18）：题容器只挂提交按钮，各空容器按 data-sub-type 复用单题题型引擎
          q.querySelectorAll('[data-sub-id]').forEach(function(sub) {
            var st = sub.getAttribute('data-sub-type') || '';
            if (st === 'drag') initDrag(sub);
            else if (st === 'sort') initSort(sub);
            else if (st === 'match') initMatch(sub);
            else if (st === 'memory') initMemory(sub);
            else if (st === 'choice') initChoice(sub);
            else if (st === 'judge') initJudge(sub);
            else if (st === 'annotate') initAnnotate(sub);
            else if (st === 'voice_read') initVoiceRead(sub);
            else if (st === 'voice_speak' || st === 'voice') initVoiceSpeak(sub);
            else if (st === 'combo_composition') bindComboPrefill(q, sub); // 组合空预填充（2026-10-21）
            // 只读卡也挂 ✍手写(2026-10-29): 教师端批改卡查看学生笔迹的唯一入口——面板挂 body 级
            // 不受题容器只读 CSS 约束; 面板内书写改动只落输入框不进提交(collector 只读拦截)
            enhanceTextInputs(sub);
          });
        }
        else if (qtype === 'lecture' || qtype === 'reflection') {
          // 讲解/反思页：无题级交互引擎（反思书写区由 restore/reflection 收集器按 textarea 处理）
        }
        else {
          console.warn('[question-runtime] 题级 data-question-type="' + qtype + '" 是已退役的单题形态——作答题必须为 exercise 练习页+data-sub-type 子题容器，本题交互引擎不绑定');
        }
        enhanceTextInputs(q);
        initRatingStars(q);
        if (!RO) { bindSubmit(q); bindClear(q); }
        hideFeedback(q);
      } catch (err) { console.error('[question-runtime] init ' + qtype + ' failed:', err); }
    });
    initNarration();
    // 阻断原生拖拽：题目HTML若含 img/draggable 元素，浏览器原生拖拽会停发 mousemove，自制拖拽就卡住不动
    document.addEventListener('dragstart', function(e) {
      if (e.target.closest && e.target.closest('[data-drag-id],[data-sort-id]')) e.preventDefault();
    });
    // document 级拖拽事件委托（一次绑定，按题型路由）
    document.addEventListener('mousedown', function(e) {
      dragStart(e); sortStart(e);
    });
    document.addEventListener('mousemove', function(e) {
      dragMove(e); sortMove(e);
    });
    document.addEventListener('mouseup', function(e) {
      dragEnd(e); sortEnd(e);
    });
    document.addEventListener('touchstart', function(e) { dragStart(e); sortStart(e); }, { passive: false });
    document.addEventListener('touchmove', function(e) { dragMove(e); sortMove(e); }, { passive: false });
    document.addEventListener('touchend', function(e) { dragEnd(e); sortEnd(e); });
    window.addEventListener('resize', function() {
      document.querySelectorAll('[data-question-id]').forEach(function(q) { if (getQType(q) === 'match') drawMatchLines(q); });
      // 复合题内的 match 空容器同样需要重画连线
      document.querySelectorAll('[data-sub-id][data-sub-type="match"]').forEach(function(sub) { drawMatchLines(sub); });
    });
  }

  // 回显监听（runtime 先注册，collector 300ms 后触发，时序安全）
  window.addEventListener('collector-restore-done', function(e) {
    var d = e.detail || {};
    document.querySelectorAll('[data-question-id]').forEach(function(q) {
      var qtype = getQType(q);
      if (qtype !== d.qType) return;
      var fn = RESTORERS[qtype];
      if (fn) { try { fn(q, d); } catch (err) { console.error('[question-runtime] restore ' + qtype + ' failed:', err); } }
      // 教师演示模式(2026-10-29): 回显把已判分选择锁定(data-locked 挡切换+原生控件disabled),
      // 解锁供老师改选演示; 不用 unlockAllChoiceScopes——它连对错着色一起清, 着色是学生
      // 批改结果的视觉信息(老师演示前要看学生错在哪), 只解交互不清色
      if (window.__TEACHER_DEMO__) {
        q.querySelectorAll('[data-locked]').forEach(function(scope) {
          scope.removeAttribute('data-locked');
          scope.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach(function(i) { i.disabled = false; });
        });
      }
    });
  });

  // 订正重试：与 answer-collector 的 resetForRetry 同消息触发，解锁选择/判断题并清对错着色
  window.addEventListener('message', function(event) {
    if (event.data && event.data.type === 'retry') unlockAllChoiceScopes();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
`;
